import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import { Separator } from '../../../../foundation/altzero_base/base/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../foundation/altzero_base/base/components/ui/tabs';
import { ChevronLeft, ChevronRight, CheckCircle, FileText, Edit3, Eye, Save, Loader2, Calendar, LayoutGrid, Table2, Plus, X, RefreshCw, Wand2 } from 'lucide-react';
import { ParsedOffer } from '../../contexts/BatchParsingContext';
import { reparseOfferSection, SectionKey } from '../../services/batchParsingService';
import { emailApiService } from '../../services/email/emailApiService';
import { useDocumentForm } from '../../contexts/DocumentFormContext';
import { usePowerEnvDefaultsState } from '../../hooks/usePowerEnvDefaults';
import { useNonEnergyDefaults } from '../../hooks/useNonEnergyDefaults';
import { ENV_SCHEMES, resolveEnvDisplayKey, resolveEnvSchemeLabel, resolvePowerPct, extractCalendarYear, type PowerEnvYearConfig } from '../../lib/envSchemeRegistry';
import { resolveNonEnergyDefaults, isSupplementaryKey, isMeteringTierKey } from '../../lib/nonEnergyDefaultsRegistry';
import { termSpanDaysFromDates } from '../../lib/termWeight';
import { rebaseTermYearRanges, getExpectedYearsForTerm, getEditableYearsForTerm, removeTermYear, envYearRangeFromTerms } from '../../lib/termDateRanges';
import { rebasedEnvYearRange } from '../../lib/envYearRenumber';
import { expandSteppedAgreementTerms } from '../../lib/steppedAgreementTerms';
import type { TenderTermSeries } from '../../lib/tenderTermSeries';
import {
    roundEnvRate, ENERGY_RATE_STEP, ENV_RATE_STEP, CERT_PRICE_STEP, POWER_PCT_STEP,
} from '../../lib/ratePrecision';
import {
    formatTermKeyLabel, formatRateYearLabel, formatRateYearRange, formatTermSeriesLabels,
    termNeedsYearMapping, termYearChoices, duplicateTermYears, normaliseTermYearMapping,
    getActiveTermYearMapping, resolveAssignedTermYear,
    type TermYearMapping,
} from '../../lib/termLabel';
import { useToast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { getActiveRetailers } from '../../services/retailerService';

// Helper to format non-energy charge labels (replace "Network Service" with "Other Service Charge")
const formatChargeLabel = (chargeName: string): string => {
    if (chargeName.toLowerCase() === 'network service' || chargeName.toLowerCase() === 'networkservice') {
        return 'Other Service Charge';
    }
    return chargeName;
};

// The three non-energy charge fields the business actually uses. These always render in the
// review panel even when the offer document stated none of them, so a reviewer can see what's
// missing and pull the configured default in with the wand button — an absent field is
// invisible otherwise, which is how retailer defaults silently went unnoticed before.
type CanonicalNecField = 'meterAgreement' | 'supplementaryMetering' | 'serviceCharge';

const CANONICAL_NEC_FIELDS: { key: CanonicalNecField; label: string }[] = [
    { key: 'meterAgreement', label: 'Meter Agreement' },
    { key: 'supplementaryMetering', label: 'Supplementary Metering' },
    { key: 'serviceCharge', label: 'Other Service Charge' },
];

/**
 * Find the key an existing charges object actually uses for a canonical field, so an edit
 * updates the field in place instead of adding a near-duplicate under a different spelling
 * (parsed docs use snake_case, "Meter Agreement", "supply_charge", … interchangeably).
 * Returns the canonical key itself when no existing variant is present.
 */
const resolveNecFieldKey = (charges: Record<string, unknown> | null, field: CanonicalNecField): string => {
    const keys = Object.keys(charges || {});
    const match = keys.find(k => {
        if (field === 'supplementaryMetering') return isSupplementaryKey(k);
        if (field === 'meterAgreement') return !isSupplementaryKey(k) && isMeteringTierKey(k);
        // serviceCharge — anything service/supply shaped, but not a metering tier
        const norm = k.toLowerCase().replace(/_/g, ' ');
        return !isSupplementaryKey(k) && !isMeteringTierKey(k)
            && (norm.includes('service') || norm.includes('supply charge'));
    });
    return match || field;
};

// Rate-escalation year INSIDE a contract term — rendered plain "Year 1", the original
// wording. A term tab is also labelled "Year N", so the two read alike on this screen and are
// told apart by position: the tab names the term, the rows name the years inside it. Nothing
// matches on the text (the proposal pairs costs via the hidden __TERM_N__ stamp), so the
// overlap is cosmetic.
const formatYearLabel = (yearKey: string): string => formatRateYearLabel(yearKey);

// Contract term for display: "Year 2", "Year 3 (Stepped)".
// Handles plain numbers ("10", "22") and variant keys ("46_stepped", "46_smooth").
const formatContractTermLabel = (termKey: string): string => formatTermKeyLabel(termKey);

// Helper to format date range for display
const formatDateRange = (dateRange: { startDate?: string | null; endDate?: string | null } | undefined): string => {
    if (!dateRange || (!dateRange.startDate && !dateRange.endDate)) return '';
    if (dateRange.startDate && dateRange.endDate) {
        return `${dateRange.startDate} - ${dateRange.endDate}`;
    }
    if (dateRange.startDate) return `From ${dateRange.startDate}`;
    if (dateRange.endDate) return `Until ${dateRange.endDate}`;
    return '';
};

// Rate-year span inside a term (e.g., "Year 1", "Year 1-2") — see formatYearLabel.
const getYearRangeLabel = (yearKeys: string[]): string => {
    if (yearKeys.length === 0) return '';
    if (yearKeys.length === 1) return formatYearLabel(yearKeys[0]);
    const firstYear = parseInt(yearKeys[0].replace(/\D/g, '')) || 0;
    const lastYear = parseInt(yearKeys[yearKeys.length - 1].replace(/\D/g, '')) || 0;
    return formatRateYearRange(firstYear, lastYear);
};

// Helper to get rate type view data (grouped by peak/offpeak/shoulder)
// Groups years by contract term (e.g., "Year 1-2" for 24-month contract)
const getRateTypeViewData = (contractTerms: any): { rateType: 'peak' | 'offpeak' | 'shoulder'; term: string; yearRange: string; yearKeys: string[]; values: { yearKey: string; value: number | null; dateRange?: string }[] }[] => {
    const result: { rateType: 'peak' | 'offpeak' | 'shoulder'; term: string; yearRange: string; yearKeys: string[]; values: { yearKey: string; value: number | null; dateRange?: string }[] }[] = [];

    if (!contractTerms) return result;

    Object.keys(contractTerms).forEach(term => {
        const termData = contractTerms[term];
        if (!termData) return;

        const yearKeys = getAllYearKeys(termData).filter(key => key.startsWith('year_'));
        if (yearKeys.length === 0) return;

        // Group by rate type - one entry per contract term
        ['peak', 'offpeak', 'shoulder'].forEach(rateType => {
            const rateTypeKey = rateType as 'peak' | 'offpeak' | 'shoulder';
            const yearRangeLabel = getYearRangeLabel(yearKeys);

            // Collect all years' data for this rate type
            const yearValues: { yearKey: string; value: number | null; dateRange?: string }[] = [];

            yearKeys.forEach(yearKey => {
                const yearData = termData[yearKey];
                if (!yearData) return;

                const value = yearData[rateTypeKey] ?? null;
                // Support both camelCase and snake_case for dateRange
                const dateRangeObj = yearData.dateRange || yearData.date_range;
                const dateRange = formatDateRange(dateRangeObj ? {
                    startDate: dateRangeObj.startDate || dateRangeObj.start_date,
                    endDate: dateRangeObj.endDate || dateRangeObj.end_date
                } : undefined);

                yearValues.push({
                    yearKey,
                    value,
                    dateRange: dateRange || undefined
                });
            });

            // Only add if there's at least one year with data
            if (yearValues.length > 0) {
                result.push({
                    rateType: rateTypeKey,
                    term,
                    yearRange: yearRangeLabel,
                    yearKeys,
                    values: yearValues
                });
            }
        });
    });

    return result;
};

// Helper to get all years with data (filters out null values for display, but keeps all for editing)
const getYearsWithData = (ratesObj: Record<string, any> | undefined): [string, any][] => {
    if (!ratesObj) return [];
    return Object.entries(ratesObj).filter(([_, value]) => value !== null && value !== undefined);
};

// Helper to get all year keys (for editing mode - shows all available year slots)
const getAllYearKeys = (ratesObj: Record<string, any> | undefined): string[] => {
    if (!ratesObj) return [];
    return Object.keys(ratesObj).sort((a, b) => {
        // Sort year_1, year_2, etc. numerically
        const numA = parseInt(a.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.replace(/\D/g, '')) || 0;
        return numA - numB;
    });
};

/**
 * Rate Year numbers a given environmental year slot is allowed to move to.
 *
 * Every number from 1 up to one past the highest year the scheme already uses, minus the ones
 * already occupied (the slot's own number always stays selectable). Offering only free numbers
 * means a move can never overwrite a quoted year, so the reviewer is never presented with a
 * choice that would have to be refused.
 */
export const envYearOptions = (yearData: Record<string, any> | undefined, currentYear: string): number[] => {
    const taken = new Set(
        Object.keys(yearData || {})
            .filter(k => /^year_\d+$/.test(k))
            .map(k => parseInt(k.slice(5), 10))
            .filter(n => !isNaN(n)),
    );
    const current = parseInt(currentYear.slice(5), 10) || 1;
    const ceiling = Math.max(current, ...(taken.size > 0 ? [...taken] : [1])) + 1;
    const options: number[] = [];
    for (let n = 1; n <= ceiling; n++) {
        if (n === current || !taken.has(n)) options.push(n);
    }
    return options;
};

/**
 * The Rate Year slot an "Add Year" click should create: the LOWEST free number.
 *
 * Lowest, not "one past the end", precisely so a scheme whose parse began at year_2 gets a
 * year_1 — that is the case this exists for. Capped at one past the highest occupied slot so
 * the button cannot manufacture an unbounded run of empty years, and returns null when the
 * scheme is already contiguous from 1 to that cap.
 */
export const nextFreeEnvYearSlot = (yearData: Record<string, unknown> | undefined): number | null => {
    const taken = new Set(
        Object.keys(yearData || {})
            .map(k => /^year_(\d+)$/.exec(k)?.[1])
            .filter((n): n is string => !!n)
            .map(Number)
            .filter(n => Number.isFinite(n) && n > 0),
    );
    const ceiling = (taken.size > 0 ? Math.max(...taken) : 0) + 1;
    for (let n = 1; n <= ceiling; n++) {
        if (!taken.has(n)) return n;
    }
    return null;
};

// Check if data uses snake_case format
const isSnakeCaseFormat = (data: any): boolean => {
    return !!data?.contract_terms;
};

// Check if data is in new contractTerms format (supports both camelCase and snake_case)
const isNewContractTermsFormat = (data: any): boolean => {
    const contractTerms = data?.contractTerms || data?.contract_terms;
    return contractTerms && typeof contractTerms === 'object' && contractTerms !== null;
};

// Get contract terms object (supports both camelCase and snake_case)
const getContractTermsData = (data: any): any => {
    return data?.contractTerms || data?.contract_terms || null;
};

// Get the contract terms key name based on format
const getContractTermsKey = (data: any): string => {
    return data?.contract_terms ? 'contract_terms' : 'contractTerms';
};

/**
 * Show the shorter terms a single stepped agreement also offers, instead of the one row it was
 * transcribed as.
 *
 * ⚠️ THIS IS NOT A NEW CALCULATION — it is the one the costing already performs, made visible.
 *
 * calculationRoutes runs expandSteppedAgreementTerms on the way into costing, so Origin's single
 * 30-month agreement has always been compared at 6, 18 and 30 months. The reviewer was shown the
 * bare "30" and had no way to see, edit or sanity-check the two terms the tender would actually
 * be priced on. Engie transcribes the same shape as three terms and has always displayed as
 * three, which is what made the two look inconsistent when they never were.
 *
 * Running the SAME function with the SAME series the server uses (tender.extn.term_series, which
 * is what useDocumentForm exposes) is what keeps the screen and the costing from ever drifting.
 *
 * ⚠️ FIRST LOAD ONLY — see the caller.
 *
 * Expansion never overwrites a quoted term and returns its input unchanged when there is nothing
 * to expand, so an offer that lists its own terms is untouched. But it is not the user's last
 * word: re-running it on an approved offer would resurrect a sub-term the reviewer had
 * deliberately deleted, exactly as re-baking the environmental values would undo a deletion
 * there. So the caller applies it only to a raw parse.
 */
const expandAgreementTerms = (data: any, series: TenderTermSeries | null | undefined): any => {
    const contractTerms = getContractTermsData(data);
    if (!contractTerms) return data;
    const expanded = expandSteppedAgreementTerms(contractTerms, series);
    if (expanded === contractTerms) return data;   // nothing to expand — keep the same object
    return { ...data, [getContractTermsKey(data)]: expanded };
};

/**
 * The reviewer's term -> year assignment, as stored on the offer.
 *
 * Lives alongside contract_terms in the same JSONB blob, so no migration. Absent on every
 * existing offer, and absence means "use the derived label" — see termLabel.ts.
 */
const TERM_YEAR_MAPPING_KEY = 'term_year_labels';
// Returns {} rather than null for an unmapped offer, deliberately. Passing null would let the
// label functions fall back to the TENDER's assignment (see termLabel.ts), and this screen
// edits one offer — inheriting another offer's numbers here would show the reviewer something
// they never set for this document.
const getTermYearMapping = (data: any): TermYearMapping =>
    (data?.[TERM_YEAR_MAPPING_KEY] as TermYearMapping | undefined) ?? {};

// Get smooth data (supports both camelCase and snake_case)
const getSmoothData = (data: any): any => {
    return data?.smooth || null;
};

// Get non-energy charges (supports both camelCase and snake_case)
const getNonEnergyCharges = (smoothData: any): any => {
    return smoothData?.nonEnergyCharges || smoothData?.non_energy_charges || null;
};

// Get the non-energy charges key name based on format
const getNonEnergyChargesKey = (smoothData: any): string => {
    return smoothData?.non_energy_charges ? 'non_energy_charges' : 'nonEnergyCharges';
};

// Get environmental charges (supports both camelCase and snake_case)
const getEnvironmentalCharges = (smoothData: any): any => {
    return smoothData?.environmentalCharges || smoothData?.environmental_charges || null;
};

// Get the environmental charges key name based on format
const getEnvironmentalChargesKey = (smoothData: any): string => {
    return smoothData?.environmental_charges ? 'environmental_charges' : 'environmentalCharges';
};

// Get date range (supports both camelCase and snake_case)
const getDateRange = (obj: any): { startDate?: string | null; endDate?: string | null } | undefined => {
    const dateRange = obj?.dateRange || obj?.date_range;
    if (!dateRange) return undefined;
    return {
        startDate: dateRange.startDate || dateRange.start_date || null,
        endDate: dateRange.endDate || dateRange.end_date || null
    };
};

// Get the date range key names based on format
/**
 * A term's own date range, or the span its priced years cover.
 *
 * A term block usually carries no date_range of its own — the dates sit one level down, on
 * each year slot. Reading only the term level printed "No date range specified" on screens
 * whose rows below were showing dates the whole time. Falls back to the earliest start and
 * latest end across the years, which is the term's span by definition.
 */
const getTermDateRange = (termData: any): { startDate?: string | null; endDate?: string | null } | undefined => {
    const own = getDateRange(termData);
    if (own?.startDate || own?.endDate) return own;
    if (!termData || typeof termData !== 'object') return undefined;

    const starts: string[] = [];
    const ends: string[] = [];
    Object.entries(termData).forEach(([key, value]) => {
        if (!/^year_\d+$/.test(key)) return;
        const r = getDateRange(value);
        if (r?.startDate) starts.push(r.startDate);
        if (r?.endDate) ends.push(r.endDate);
    });
    if (starts.length === 0 && ends.length === 0) return undefined;

    return {
        startDate: starts.length ? starts.slice().sort()[0] : null,
        endDate: ends.length ? ends.slice().sort()[ends.length - 1] : null,
    };
};

/**
 * Which spelling to WRITE a date range in.
 *
 * snake_case unless the object already uses camelCase. It used to be the other way round —
 * `!obj?.date_range` is true for an empty slot as well as for a camelCase one, so editing a
 * date on a slot that had none wrote `dateRange/startDate/endDate` into an object whose
 * siblings all used `date_range/start_date/end_date`. On prod that left 130 year slots in the
 * minority spelling, invisible to every reader that checks only snake_case.
 *
 * snake_case is the parser's own shape and what the great majority of stored slots use, so a
 * slot with nothing to go on follows it.
 */
const getDateRangeKeys = (obj: any): { dateRangeKey: string; startKey: string; endKey: string } => {
    const isCamel = !obj?.date_range && !!obj?.dateRange;
    return {
        dateRangeKey: isCamel ? 'dateRange' : 'date_range',
        startKey: isCamel ? 'startDate' : 'start_date',
        endKey: isCamel ? 'endDate' : 'end_date'
    };
};

// Get rate value from year data (supports both camelCase and snake_case for nested fields)
const getYearRateData = (yearData: any): { peak: number | null; offpeak: number | null; shoulder: number | null; dateRange?: any } => {
    return {
        peak: yearData?.peak ?? null,
        offpeak: yearData?.offpeak ?? null,
        shoulder: yearData?.shoulder ?? null,
        dateRange: getDateRange(yearData)
    };
};

// Get environmental year data (supports both camelCase and snake_case).
// Applies formula derivation when ratePerKwh is missing:
//   ratePerKwh = (pricePerCertificate * powerPercentage) / 1000
// When powerPercentage is also missing, uses sys_config defaults for the scheme.
const getEnvYearData = (
    yearData: any,
    schemeKey?: string,
    defaults?: PowerEnvYearConfig,
    /** The scheme's other year slots — lets a CUMULATIVE schedule resolve by end date. */
    siblingSlots?: Record<string, any> | null
): { ratePerKwh: number | null; powerPercentage: number | null; pricePerCertificate: number | null } => {
    const rate = yearData?.ratePerKwh ?? yearData?.rate_per_kwh ?? null;
    const pct  = yearData?.powerPercentage ?? yearData?.power_percentage ?? null;
    const cert = yearData?.pricePerCertificate ?? yearData?.price_per_certificate ?? null;

    // Year-wise lookup: resolve the default for THIS year's own calendar year (from its
    // date_range), falling back to the nearest configured year if this exact one isn't set.
    const defaultPct = schemeKey ? resolvePowerPct(defaults, resolveEnvDisplayKey(schemeKey), extractCalendarYear(yearData, siblingSlots)) : 0;
    // Always use defaults for power% when not provided by the document
    const effectivePct = (pct !== null && pct > 0) ? pct : defaultPct;

    // Rate already present — still show power% from defaults if missing
    if (rate !== null && rate > 0) {
        return { ratePerKwh: rate, powerPercentage: effectivePct > 0 ? effectivePct : pct, pricePerCertificate: cert };
    }

    // No cert — can't derive rate
    if (!cert || cert <= 0) {
        return { ratePerKwh: rate, powerPercentage: effectivePct > 0 ? effectivePct : pct, pricePerCertificate: cert };
    }

    // Derive rate using formula: (cert × effectivePct) / 1000
    if (effectivePct <= 0) {
        return { ratePerKwh: rate, powerPercentage: pct, pricePerCertificate: cert };
    }

    return {
        // Rounded to ENV_RATE_DECIMALS purely to strip float noise
        // (0.12498789776700002 -> 0.1249878978), matching the field's own step. NOT a
        // shortening — see ratePrecision.ts on why 4dp was too coarse for these rates.
        ratePerKwh: roundEnvRate((cert * effectivePct) / 1000),
        powerPercentage: effectivePct,
        pricePerCertificate: cert,
    };
};

// Unlike getEnvYearData above (which deliberately PRESERVES an existing real rate — it's meant
// for read-only display, never overriding a genuine AI-parsed value with a guess), this ALWAYS
// recomputes ratePerKwh = (cert × pct) / 1000 regardless of what's currently there. This is what
// the manual "Apply Formula" wand button needs: the user clicked it specifically to override
// whatever's in the field right now. Using getEnvYearData for that button meant it silently did
// nothing whenever a real rate already existed (its "rate already present, keep as-is" branch),
// which only looked like it worked after first deleting the rate to make that branch fall through.
const forceApplyFormula = (
    yearData: any,
    schemeKey?: string,
    defaults?: PowerEnvYearConfig,
    /** The scheme's other year slots — lets a CUMULATIVE schedule resolve by end date. */
    siblingSlots?: Record<string, any> | null
): { ratePerKwh: number | null; powerPercentage: number | null; pricePerCertificate: number | null } => {
    const pct = yearData?.powerPercentage ?? yearData?.power_percentage ?? null;
    const cert = yearData?.pricePerCertificate ?? yearData?.price_per_certificate ?? null;

    // Year-wise lookup: resolve the default for THIS year's own calendar year (from its
    // date_range), falling back to the nearest configured year if this exact one isn't set.
    const defaultPct = schemeKey ? resolvePowerPct(defaults, resolveEnvDisplayKey(schemeKey), extractCalendarYear(yearData, siblingSlots)) : 0;
    const effectivePct = (pct !== null && pct > 0) ? pct : defaultPct;

    if (!cert || cert <= 0 || effectivePct <= 0) {
        return { ratePerKwh: null, powerPercentage: effectivePct > 0 ? effectivePct : pct, pricePerCertificate: cert };
    }

    return {
        ratePerKwh: roundEnvRate((cert * effectivePct) / 1000),
        powerPercentage: effectivePct,
        pricePerCertificate: cert,
    };
};

// Get the environmental field key names based on format
const getEnvFieldKeys = (yearData: any): { rateKey: string; percentKey: string; certKey: string } => {
    const isSnake = yearData?.rate_per_kwh !== undefined;
    return {
        rateKey: isSnake ? 'rate_per_kwh' : 'ratePerKwh',
        percentKey: isSnake ? 'power_percentage' : 'powerPercentage',
        certKey: isSnake ? 'price_per_certificate' : 'pricePerCertificate'
    };
};

/**
 * The date range a newly-created environmental Rate Year should carry.
 *
 * ⚠️ A ROW WITH NO RANGE CANNOT RESOLVE ITS POWER % — see envYearRangeFromTerms.
 *
 * Preference order is "what the retailer actually quoted" before "what the contract implies":
 * an existing scheme's same-numbered slot holds the document's own period, and every scheme in
 * one document is quoted against the same periods, so copying it is exact. With no scheme to
 * copy from — the reviewer adding the offer's FIRST environmental charge — the offer's contract
 * start gives the anniversary window instead.
 *
 * Null when neither is available, and the caller then leaves the row undated rather than
 * inventing a period.
 */
const seedEnvYearRange = (
    data: any,
    yearKey: string,
    sampleScheme?: Record<string, any> | null,
): Record<string, any> | null => {
    const quoted = sampleScheme?.[yearKey]?.date_range ?? sampleScheme?.[yearKey]?.dateRange;
    if (quoted && (quoted.start_date ?? quoted.startDate)) return quoted;
    const index = parseInt(/^year_(\d+)$/.exec(yearKey)?.[1] ?? '', 10);

    /**
     * ⚠️ HAND YEAR 1'S REAL END OVER, SO YEAR 2 STARTS WHERE IT ACTUALLY FINISHES.
     *
     * A scheme can hold a quoted Year 1 and nothing after it — which is how AGL ends up with a
     * fifteen-month year_1 beside generated years that assumed twelve. The two overlapped by a
     * quarter and the per-year average counted it twice. See envYearRangeFromTerms.
     */
    const year1Quoted = sampleScheme?.year_1?.date_range ?? sampleScheme?.year_1?.dateRange;
    const year1End = year1Quoted?.end_date ?? year1Quoted?.endDate ?? null;
    return envYearRangeFromTerms(getContractTermsData(data), index, year1End);
};

// Get available contract terms from parsed data
// Includes both plain keys ("10", "22") and variant keys ("46_stepped", "46_smooth")
const getAvailableContractTerms = (data: any): string[] => {
    const contractTerms = getContractTermsData(data);
    if (!contractTerms) return [];
    return Object.keys(contractTerms)
        .filter(key => {
            const numericPart = key.split('_')[0];
            return !isNaN(Number(numericPart)) && Number(numericPart) > 0;
        })
        .sort((a, b) => {
            const numA = Number(a.split('_')[0]);
            const numB = Number(b.split('_')[0]);
            if (numA !== numB) return numA - numB;
            return a.localeCompare(b);
        });
};

// Filter contract terms by rate type when Mixed — only show smooth or stepped terms
const getFilteredContractTerms = (data: any, rateFilter: 'Smooth' | 'Stepped'): string[] => {
    const all = getAvailableContractTerms(data);
    const contractTerms = getContractTermsData(data) || {};
    const suffix = rateFilter === 'Smooth' ? '_smooth' : '_stepped';
    const opposite = rateFilter === 'Smooth' ? '_stepped' : '_smooth';
    return all.filter(key => {
        if (key.endsWith(suffix)) return true;
        if (key.endsWith(opposite)) return false;
        // Plain key — use stamped rate_type to route to exactly one tab
        const termData = contractTerms[key];
        if (termData?.rate_type) {
            return rateFilter === 'Smooth' ? termData.rate_type === 'Smooth' : termData.rate_type === 'Stepped';
        }
        // Fallback for older offers without rate_type: show in both tabs
        const months = key.split('_')[0];
        return !all.some(k => k.startsWith(months + '_'));
    });
};

// Determine the display rate type from parsed data (never hardcoded)
const getDisplayRateType = (data: any): 'Smooth' | 'Stepped' | 'Mixed' => {
    // Key/per-term check first — globalRt can lie (e.g. EA sets rate_type='Stepped' even on mixed offers)
    const contractTerms = getContractTermsData(data);
    if (contractTerms) {
        const keys = Object.keys(contractTerms);
        // Any key without '_stepped' suffix is smooth (plain "24", "36", or "24_smooth" etc.)
        const hasSteppedKey = keys.some(k => k.includes('_stepped'));
        const hasSmoothKey = keys.some(k => !k.includes('_stepped'));
        if (hasSteppedKey && hasSmoothKey) return 'Mixed';
        if (hasSteppedKey) return 'Stepped';
        if (hasSmoothKey) {
            // Confirm it's not all just non-term metadata keys — check per-term rate_type
            const termValues = Object.values(contractTerms) as any[];
            const hasStepped = termValues.some(t => t?.rate_type === 'Stepped');
            const hasSmooth = termValues.some(t => t?.rate_type === 'Smooth');
            if (hasStepped && hasSmooth) return 'Mixed';
            if (hasStepped) return 'Stepped';
        }
    }

    // Fall back to explicit global rate type
    const globalRt = data?.rateType || data?.rate_type;
    if (globalRt === 'Smooth' || globalRt === 'Stepped') return globalRt;

    return 'Smooth';
};

// Get expiry/validity date (supports both camelCase and snake_case)
const getExpiryDate = (data: any): string | null => {
    return data?.expiryDate || data?.expiry_date || data?.validityDate || data?.validity_date || null;
};

// Get the expiry date key based on format
const getExpiryDateKey = (data: any): string => {
    if (data?.expiry_date !== undefined) return 'expiry_date';
    if (data?.validity_date !== undefined) return 'validity_date';
    return 'expiryDate';
};

interface OfferReviewComparisonProps {
    offers: ParsedOffer[];
    currentIndex: number;
    onNavigate: (index: number) => void;
    onApprove: (offerId: string, editedData?: any) => void | Promise<void>;
    /**
     * Persist an in-progress edit WITHOUT marking the offer reviewed — used by every
     * navigation path. Falls back to onApprove when absent only so an older caller keeps
     * working; every caller in this app passes it.
     */
    onSaveEdits?: (offerId: string, editedData?: any) => void | Promise<void>;
    onReparse?: (offerId: string) => void;
}

// Exposed to the parent (EmailProcessingStep) so its own "Next" button — separate from this
// screen's own "Save & Approve" — can capture whatever's currently being edited here before
// navigating away. Without this, clicking "Next" directly (instead of "Save & Approve" first)
// silently discarded any in-progress edit: onApprove/markAsReviewed never fired for it, so the
// hand-rolled reconstruction that "Next" runs fell back to the ORIGINAL un-edited parsedData.
// Returns the flushed {offerId, editedData} (or undefined if there's no current offer) — the
// caller passes this straight into its own reconstruction rather than re-reading its own
// parsedOffers state, which a React closure captured BEFORE this flush and won't reflect the
// edit yet even after awaiting this call (state updates re-render later, not retroactively).
export interface OfferReviewComparisonHandle {
    flushCurrentEdit: () => (Promise<{ offerId: string; editedData: any } | undefined> | { offerId: string; editedData: any } | undefined);
}

export const OfferReviewComparison = React.forwardRef<OfferReviewComparisonHandle, OfferReviewComparisonProps>(({
    offers,
    currentIndex,
    onNavigate,
    onApprove,
    onSaveEdits,
    onReparse,
}, ref) => {
    const currentOffer = offers[currentIndex];
    const { sites, termSeries } = useDocumentForm();
    const { defaults: powerEnvDefaults, loaded: powerEnvDefaultsLoaded } = usePowerEnvDefaultsState();
    const { toast } = useToast();
    const nonEnergyDefaults = useNonEnergyDefaults();
    // Retailer NAME (not id) is what resolves this offer's non-energy defaults — the offer only
    // carries assignment.retailerId, so map it here rather than threading a new prop through.
    const [retailerNames, setRetailerNames] = useState<Record<string, string>>({});
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);
    /**
     * Whether the loaded document is actually a PDF. Offers arrive as spreadsheets and Word
     * files too — the manual uploader accepts .xlsx/.xls/.docx/.doc, and 13,549 emailed
     * attachments are not PDFs — and an iframe cannot render any of them. Null until loaded.
     */
    const [isPdfDocument, setIsPdfDocument] = useState<boolean | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState<any>(currentOffer?.parsedData || {});
    // `unit` is what the entered figure is quoted in — the row converts it to the annual value
    // the tender model stores ($/mo x 12, $/day x 365). The type omitted it while the JSX both
    // set and read it, which was the file's only build error.
    const [newNECField, setNewNECField] = useState<{
        label: string;
        value: string;
        unit: 'year' | 'month' | 'day';
    } | null>(null);
    const [newEnvScheme, setNewEnvScheme] = useState<string | null>(null);
    const [contractTermsViewMode, setContractTermsViewMode] = useState<'terms' | 'rates'>('terms');
    const [selectedRateFilter, setSelectedRateFilter] = useState<'Smooth' | 'Stepped'>(() => {
        const d = currentOffer?.editedData || currentOffer?.parsedData || {};
        return getDisplayRateType(d) === 'Stepped' ? 'Stepped' : 'Smooth';
    });
    const [addTermPanel, setAddTermPanel] = useState<{ months: number; rateType: 'Smooth' | 'Stepped' } | null>(null);
    const [editingTermKey, setEditingTermKey] = useState<string | null>(null);
    const [editTermConfig, setEditTermConfig] = useState<{ months: number; rateType: 'Smooth' | 'Stepped' } | null>(null);
    const [reparsingSections, setReparsingSections] = useState<Partial<Record<SectionKey, boolean>>>({});
    const sectionControllersRef = useRef<Partial<Record<SectionKey, AbortController>>>({});

    const handleSectionReparse = async (section: SectionKey) => {
        const controller = new AbortController();
        sectionControllersRef.current[section] = controller;
        setReparsingSections(prev => ({ ...prev, [section]: true }));
        try {
            const merged = await reparseOfferSection(currentOffer, section, editedData, controller.signal);
            setEditedData(merged);
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            console.error(`Section reparse failed (${section}):`, err);
        } finally {
            delete sectionControllersRef.current[section];
            setReparsingSections(prev => ({ ...prev, [section]: false }));
        }
    };

    const handleCancelSection = (section: SectionKey) => {
        sectionControllersRef.current[section]?.abort();
    };

    // Check if any site is in NSW for shoulder rate logic
    const isNSW = (sites || []).some(site => {
        const state = (site.address?.state || '').toUpperCase();
        return state === 'NSW' || state === 'NEW SOUTH WALES';
    });

    // Derive the active rate side so env/NEC sections track the user's Smooth/Stepped selection
    const activeRateSide = selectedRateFilter === 'Stepped' && editedData.stepped ? 'stepped' : 'smooth';
    const activeRateData = editedData[activeRateSide] || editedData.smooth;

    // Load PDF when current offer changes
    useEffect(() => {
        if (currentOffer) {
            // Bake formula-derived env values in ONLY the very first time this offer is loaded
            // (raw AI parse, never reviewed) — so View/Edit mode agree on a sensible starting
            // number. Once the user has approved it at least once, currentOffer.editedData exists
            // and is trusted as final; re-baking on every reload (e.g. right after clicking Save &
            // Approve, when the offer list updates and re-passes this same offer back in) would
            // otherwise silently undo a deliberate deletion by re-deriving it from cert/pct again.
            const alreadyReviewed = !!currentOffer.editedData;
            // A single stepped agreement is shown as the shorter terms it also offers, so the
            // reviewer sees and can edit the terms the costing will actually use. Raw parse only
            // — see expandAgreementTerms for why an approved offer is left alone.
            const data = alreadyReviewed
                ? currentOffer.editedData
                : expandAgreementTerms(currentOffer.parsedData || {}, termSeries);
            // Non-energy defaults are seeded by their own effect below — they depend on the
            // retailer name, which loads asynchronously and is usually not available yet here.
            //
            // ⚠️ DO NOT BAKE BEFORE THE POWER% TABLE HAS ARRIVED.
            //
            // sys_config.powerEnvironment is fetched asynchronously, and until it lands the hook
            // serves a fallback holding ONLY 2026. Baking against that wrote LREC 16.67 into
            // every year of every term — and because the bake only fills EMPTY fields, the value
            // could never be corrected afterwards: by the time the real table arrived (16.67 /
            // 19.17 / 18.98 / 18.79) the fields were no longer empty. AGL showed it most plainly,
            // since its prompt stores power_percentage: null for every environmental charge, so
            // the default is the only source and all four years froze at the first-year figure.
            //
            // The raw data is shown meanwhile; the effect below bakes the moment the table is in.
            setEditedData(alreadyReviewed || !powerEnvDefaultsLoaded
                ? data
                : bakeEnvironmentalDerivedValues(data));
            setIsEditing(false);
            setSelectedRateFilter(getDisplayRateType(data) === 'Stepped' ? 'Stepped' : 'Smooth');

            // Load the PDF
            loadPdf();
        }
    }, [currentOffer]);

    /**
     * Bake the derived environmental values once the real power% table lands.
     *
     * The load effect above skips the bake while sys_config.powerEnvironment is still in flight,
     * because the fallback table holds only 2026 and a value baked from it can never be
     * corrected (the bake fills empty fields only). This runs it the moment the real table is in.
     *
     * Kept OUT of the load effect deliberately: that effect also resets isEditing and the
     * Smooth/Stepped filter, so re-running it when the fetch settles would throw the reviewer out
     * of Edit Mode mid-keystroke. Baking is idempotent — it only ever fills what is still empty —
     * so running it a second time when the table was already cached costs nothing.
     */
    useEffect(() => {
        if (!powerEnvDefaultsLoaded || !currentOffer) return;
        // An offer that has been reviewed is trusted as final; re-deriving would undo a
        // deliberate deletion. Same gate as the load effect's `alreadyReviewed`.
        if (currentOffer.editedData) return;
        setEditedData((prev: any) => bakeEnvironmentalDerivedValues(prev));
    }, [powerEnvDefaultsLoaded, currentOffer]);

    const loadPdf = async () => {
        if (!currentOffer) return;

        setLoadingPdf(true);
        setPdfError(null);

        // Revoke previous blob URL to prevent memory leaks
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
        }

        try {
            console.log('📄 Loading PDF:', currentOffer.fileName, 'Account:', currentOffer.accountId);

            /**
             * ⚠️ NO GMAIL ACCOUNT IS NOT A REASON TO REFUSE. THE FILE IS IN STORAGE.
             *
             * This used to throw 'Account ID is missing' before it tried anything, and that is
             * exactly what a MANUALLY uploaded offer hits: handleManualUpload takes the id from
             * `accounts.find(a => a.is_active)?.id || ''`, so a tender with no connected mailbox
             * stores an empty string and every manual offer on it refused to preview — 3,319
             * attachments in prod arrived that way, 348 in the last month.
             *
             * The account is only ever needed for the server's GMAIL FALLBACK, which fires when
             * `storage_path` is empty. It never is: all 22,066 attachment rows carry one and
             * every file is present in the bucket. So send the id when we have it, and let the
             * server answer from storage when we do not.
             */
            const blob = await emailApiService.downloadAttachment(
                currentOffer.attachmentId,
                currentOffer.accountId || undefined
            );

            /**
             * ⚠️ AN IFRAME RENDERS A BLOB BY ITS TYPE, NOT BY ITS NAME.
             *
             * Six prod attachments are PDFs that their sender labelled `application/octet-stream`;
             * the blob inherits that type and the frame stays blank on a file that opens fine
             * everywhere else. The first five bytes settle it — `%PDF-` is the format's own magic
             * number — so re-label those and leave every genuine non-PDF alone.
             */
            const header = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
            const isPdfBytes = String.fromCharCode(...header) === '%PDF-';
            const viewable = isPdfBytes && blob.type !== 'application/pdf'
                ? new Blob([blob], { type: 'application/pdf' })
                : blob;

            setIsPdfDocument(isPdfBytes);
            setPdfUrl(URL.createObjectURL(viewable));
            console.log(`✅ Document loaded (${isPdfBytes ? 'pdf' : blob.type || 'unknown type'})`);
        } catch (error) {
            console.error('❌ Failed to load PDF:', error);
            setPdfError(error instanceof Error ? error.message : 'Failed to load PDF document');
        } finally {
            setLoadingPdf(false);
        }
    };

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    // Seed non-energy charges from the configured defaults, once per offer.
    //
    // Deliberately NOT folded into the offer-load effect above: this needs the retailer NAME,
    // which arrives from an async fetch that has usually not resolved on first render. Seeding
    // there would resolve to "no retailer" and blank the fields permanently. So it waits for
    // retailerNames, and a ref makes sure it fires at most once per offer — re-running would
    // overwrite whatever the reviewer has typed since.
    const seededNonEnergyOfferIds = useRef<Set<string>>(new Set());
    useEffect(() => {
        if (!currentOffer) return;
        if (currentOffer.editedData) return;            // already reviewed — their values win
        if (Object.keys(retailerNames).length === 0) return;  // retailer list not loaded yet
        if (seededNonEnergyOfferIds.current.has(currentOffer.id)) return;
        seededNonEnergyOfferIds.current.add(currentOffer.id);
        setEditedData((prev: any) => seedNonEnergyFromDefaults(prev));
    }, [currentOffer, retailerNames, nonEnergyDefaults]);

    // Load retailer id → name once, so the non-energy wand can resolve this offer's retailer
    // defaults. Failure is non-fatal: the wand just finds no default and stays disabled.
    useEffect(() => {
        let mounted = true;
        getActiveRetailers()
            .then(result => {
                if (!mounted || !result.success || !result.data) return;
                setRetailerNames(
                    Object.fromEntries(result.data.map(r => [r.id, r.name]))
                );
            })
            .catch(err => console.error('Failed to load retailers for non-energy defaults:', err));
        return () => { mounted = false; };
    }, []);

    const handleFieldChange = (path: string[], value: any) => {
        const newData = { ...editedData };
        let current: any = newData;
        for (let i = 0; i < path.length - 1; i++) {
            if (!current[path[i]]) current[path[i]] = {};
            current = current[path[i]];
        }
        current[path[path.length - 1]] = value;
        setEditedData(newData);
    };

    // Edits a peak/offpeak/shoulder rate on a contract term.
    //
    // ⚠️ For a SMOOTH term the value is written to EVERY year of that term, not just the row
    // being typed into. The rate table displays year_1's value in the year_2/year_3 boxes as a
    // fallback when those years have no value of their own — so typing a rate into Year 1 made
    // all three boxes show it and looked saved, while editedData still only held year_1. The
    // later steps read the real stored data, so only Year 1 kept the rate (most visible with a
    // manually entered shoulder, since peak/offpeak usually arrive parsed for every year).
    //
    // Writing every year is also semantically right: "Smooth" means one rate for the whole term.
    // Stepped terms genuinely differ per year, so they only ever write the row being edited.
    const handleRateChange = (
        term: string,
        termData: any,
        yearKey: string,
        field: 'peak' | 'offpeak' | 'shoulder',
        value: number | null,
    ) => {
        const ctKey = getContractTermsKey(editedData);
        const isSmoothTerm = !term.endsWith('_stepped') && termData?.rate_type !== 'Stepped';
        if (!isSmoothTerm) {
            handleFieldChange([ctKey, term, yearKey, field], value);
            return;
        }

        // Union of the years already present and the ones this term length implies, so a year
        // that exists only as a display placeholder still receives the value.
        const existingYears = Object.keys(termData || {}).filter(k => k.startsWith('year_'));
        const expectedYears = getExpectedYearsForTerm(parseInt(term.split('_')[0], 10));
        const allYears = Array.from(new Set([...existingYears, ...expectedYears, yearKey]));

        const existingTerms = { ...(getContractTermsData(editedData) || {}) };
        const nextTerm: Record<string, any> = { ...(existingTerms[term] || {}) };
        allYears.forEach(y => {
            nextTerm[y] = { ...(nextTerm[y] || {}), [field]: value };
        });
        existingTerms[term] = nextTerm;
        setEditedData({ ...editedData, [ctKey]: existingTerms });
    };

    // Manual "Apply Formula" trigger — user-initiated only, never automatic. Computes
    // Rate/kWh = (Cert Price × Power%) / 1000 (rounded to 4dp) for this one year/scheme card and
    // writes it into the real field. If the user deletes it afterwards, it stays deleted — this
    // never re-runs on its own.
    const handleApplyFormula = (side: 'smooth' | 'stepped', chargeType: string, year: string) => {
        const sideData = editedData[side];
        if (!sideData) return;
        const envKey = getEnvironmentalChargesKey(sideData);
        const yearData = sideData[envKey]?.[chargeType]?.[year];
        if (!yearData) return;
        const fieldKeys = getEnvFieldKeys(yearData);
        const derived = forceApplyFormula(yearData, chargeType, powerEnvDefaults, sideData[envKey]?.[chargeType]);
        const newData = { ...editedData };
        newData[side] = {
            ...sideData,
            [envKey]: {
                ...sideData[envKey],
                [chargeType]: {
                    ...sideData[envKey][chargeType],
                    [year]: {
                        ...yearData,
                        [fieldKeys.rateKey]: derived.ratePerKwh,
                        [fieldKeys.percentKey]: derived.powerPercentage,
                    },
                },
            },
        };
        setEditedData(newData);
    };

    // Power% has no "formula" to compute it FROM (unlike Rate, which derives from cert×pct) — it
    // either comes from the document or from the sys_config year-wise default directly. This is
    // the Power%-equivalent of the wand button: manually, explicitly re-apply the configured
    // default for this scheme+year, for when the user wants it back after clearing it (clearing
    // it no longer auto-refills on save — see offerAutoPopulateService's applyDerivation gate).
    const handleApplyPowerDefault = (side: 'smooth' | 'stepped', chargeType: string, year: string) => {
        const sideData = editedData[side];
        if (!sideData) return;
        const envKey = getEnvironmentalChargesKey(sideData);
        const yearData = sideData[envKey]?.[chargeType]?.[year];
        if (!yearData) return;
        const fieldKeys = getEnvFieldKeys(yearData);
        const schemes = sideData[envKey] || {};
        const scheme = schemes[chargeType];

        // ⚠️ A ROW WITH NO PERIOD USED TO FILL 0 AND LOOK BROKEN.
        //
        // The default is configured PER CALENDAR YEAR, and a slot's year comes from its date
        // range. Rows added by hand are born without one, so there was no year to look up and
        // resolvePowerPct — which does not guess — returned 0. Rows created from here on carry
        // their period (see seedEnvYearRange); this repairs the ones already saved without it,
        // taking the period from a scheme that WAS quoted with dates, or from the offer's own
        // contract start. Stamped onto the row, not just used and thrown away, so the grid, the
        // save and the costing all read the same period afterwards.
        const quoted = Object.values(schemes).find(
            (sc: any) => sc?.[year]?.date_range || sc?.[year]?.dateRange,
        ) as Record<string, any> | undefined;
        const range = extractCalendarYear(yearData, scheme) === null
            ? seedEnvYearRange(editedData, year, quoted)
            : null;
        const dated = range ? { ...yearData, date_range: range } : yearData;

        const defaultPct = resolvePowerPct(powerEnvDefaults, resolveEnvDisplayKey(chargeType), extractCalendarYear(dated, scheme));
        const newData = { ...editedData };
        newData[side] = {
            ...sideData,
            [envKey]: {
                ...schemes,
                [chargeType]: {
                    ...scheme,
                    [year]: {
                        ...dated,
                        [fieldKeys.percentKey]: defaultPct,
                    },
                },
            },
        };
        setEditedData(newData);
    };

    // Entering Edit mode should show the SAME number the user just saw in View mode — not blank
    // it out — for any environmental field that only has a value via the formula/default (never
    // actually stored). Runs once, only on the false→true transition. After that it's a normal
    // field: user can delete it and it stays deleted, or re-apply via the wand button.
    // Non-energy charges are seeded from the CONFIGURED DEFAULTS, not from the offer document.
    // The three canonical charges are Choice Energy's own standard figures — the retailer's own
    // quoted lines (Origin's $40/month service charge, its $1,260 metering tier, EnergyZone Pro)
    // are not what the tender charges, so showing them here started the reviewer from a number
    // that never matched the final tender. A retailer with no configured default stays blank
    // rather than falling back to the document's figure.
    //
    // Runs ONLY on an offer's first-ever load (same `alreadyReviewed` gate as the environmental
    // baking above) — once reviewed, the user's own values are authoritative and re-seeding would
    // silently discard their edits on every reload. Mirrors the server-side applyDerivation rule
    // in offerAutoPopulateService so screen and stored offer always agree.
    const seedNonEnergyFromDefaults = (data: any): any => {
        const d = resolveNonEnergyDefaults(nonEnergyDefaults, currentRetailerName);
        const next = { ...data };
        (['smooth', 'stepped'] as const).forEach((side) => {
            const sideData = next[side];
            if (!sideData) return;
            const necKey = getNonEnergyChargesKey(sideData);
            const existing = getNonEnergyCharges(sideData) || {};
            next[side] = {
                ...sideData,
                [necKey]: {
                    meterAgreement: nonEnergyDefaults.meterAgreement ?? null,
                    supplementaryMetering: d.supplementaryMetering ?? null,
                    serviceCharge: d.otherServiceCharge ?? null,
                    // Market fees have no configured default and are a genuine retailer
                    // pass-through — keep whatever the document stated.
                    marketFees: (existing as any).marketFees ?? (existing as any).market_fees ?? null,
                },
            };
        });
        return next;
    };

    const bakeEnvironmentalDerivedValues = (data: any): any => {
        const next = { ...data };
        (['smooth', 'stepped'] as const).forEach((side) => {
            const sideData = next[side];
            if (!sideData) return;
            const envKey = getEnvironmentalChargesKey(sideData);
            const charges = sideData[envKey];
            if (!charges) return;
            const newCharges: Record<string, any> = {};
            Object.entries(charges).forEach(([chargeType, yearsObj]: [string, any]) => {
                const newYears: Record<string, any> = {};
                getAllYearKeys(yearsObj).forEach((year) => {
                    const yearData = yearsObj[year];
                    if (!yearData) return;
                    const fieldKeys = getEnvFieldKeys(yearData);
                    const derived = getEnvYearData(yearData, chargeType, powerEnvDefaults, yearsObj);
                    newYears[year] = {
                        ...yearData,
                        [fieldKeys.rateKey]: yearData[fieldKeys.rateKey] ?? derived.ratePerKwh ?? null,
                        [fieldKeys.percentKey]: yearData[fieldKeys.percentKey] ?? derived.powerPercentage ?? null,
                    };
                });
                newCharges[chargeType] = newYears;
            });
            next[side] = { ...sideData, [envKey]: newCharges };
        });
        return next;
    };

    // Pull this retailer's configured default into a non-energy field (wand button). Mirrors the
    // Power% wand: never applied automatically, only when the reviewer asks for it — a value the
    // retailer actually quoted must never be silently replaced by a default.
    const currentRetailerName = retailerNames[currentOffer?.assignment?.retailerId || ''] || '';

    const resolveNecDefault = (field: CanonicalNecField): number | null => {
        // Meter Agreement is one static fee for every retailer (SHIELD/Powermetric).
        if (field === 'meterAgreement') return nonEnergyDefaults.meterAgreement ?? null;
        const d = resolveNonEnergyDefaults(nonEnergyDefaults, currentRetailerName);
        if (field === 'supplementaryMetering') return d.supplementaryMetering ?? null;
        return d.otherServiceCharge ?? null;
    };

    const handleApplyNecDefault = (field: CanonicalNecField) => {
        const value = resolveNecDefault(field);
        if (value === null) return; // retailer has no configured default — leave blank
        const necKey = getNonEnergyChargesKey(activeRateData);
        const fieldKey = resolveNecFieldKey(activeRateData?.[necKey] || null, field);
        handleFieldChange([activeRateSide, necKey, fieldKey], value);
    };

    const handleDeleteNECField = (fieldKey: string) => {
        const necKey = getNonEnergyChargesKey(activeRateData);
        const existing = { ...(activeRateData?.[necKey] || {}) };
        delete existing[fieldKey];
        setEditedData({
            ...editedData,
            [activeRateSide]: { ...(editedData[activeRateSide] || {}), [necKey]: existing },
        });
    };

    const handleAddNECField = (label: string, value: number) => {
        const necKey = getNonEnergyChargesKey(activeRateData);
        handleFieldChange([activeRateSide, necKey, label], value);
        setNewNECField(null);
    };

    // Add a brand-new environmental scheme card (e.g. AI missed VEET/EEIS, or a scheme the registry
    // doesn't even know about yet). Seeds the same year set as whatever schemes already exist so it
    // lines up with the contract term; user fills in Rate/Power%/Cert Price via the existing inputs.
    const handleAddEnvScheme = (rawName: string) => {
        const trimmed = rawName.trim();
        if (!trimmed) return;
        const envKey = getEnvironmentalChargesKey(activeRateData);
        const existingCharges = activeRateData?.[envKey] || {};
        const schemeKey = resolveEnvDisplayKey(trimmed);
        if (existingCharges[schemeKey]) { setNewEnvScheme(null); return; } // already exists
        const sampleYearData = Object.values(existingCharges)[0] as Record<string, any> | undefined;
        const years = sampleYearData ? getAllYearKeys(sampleYearData) : ['year_1'];
        const emptyYears = Object.fromEntries(years.map(y => {
            const range = seedEnvYearRange(editedData, y, sampleYearData);
            return [y, range ? { date_range: range } : {}];
        }));
        handleFieldChange([activeRateSide, envKey, schemeKey], emptyYears);
        setNewEnvScheme(null);
    };

    const handleDeleteEnvScheme = (chargeType: string) => {
        const envKey = getEnvironmentalChargesKey(activeRateData);
        const existing = { ...(activeRateData?.[envKey] || {}) };
        delete existing[chargeType];
        setEditedData({
            ...editedData,
            [activeRateSide]: { ...(editedData[activeRateSide] || {}), [envKey]: existing },
        });
    };

    /**
     * Remove ONE Rate Year from one environmental scheme.
     *
     * A retailer's certificate table does not always carry every year the contract term implies
     * — AGL's E&RE table quotes five periods against a term whose year count is different — so
     * the reviewer needs to drop an individual year without deleting the whole scheme card.
     *
     * The deletion is permanent, not view-local: it is written into editedData, which Save &
     * Approve sends through saveParsedDocument and then createOfferFromParsedDoc. That rebuild
     * used to re-create every year 1..maxYearNum regardless, so the year came straight back —
     * offerAutoPopulateService now emits only the year set a reviewed offer actually carries.
     */
    const handleDeleteEnvYear = (chargeType: string, year: string) => {
        const envKey = getEnvironmentalChargesKey(activeRateData);
        const schemes = { ...(activeRateData?.[envKey] || {}) };
        const years = { ...(schemes[chargeType] || {}) };
        delete years[year];
        setEditedData({
            ...editedData,
            [activeRateSide]: {
                ...(editedData[activeRateSide] || {}),
                [envKey]: { ...schemes, [chargeType]: years },
            },
        });
    };

    /**
     * Add ONE empty Rate Year to an environmental scheme, at the lowest free slot.
     *
     * ⚠️ WITHOUT THIS A DROPPED FIRST PERIOD CANNOT BE ENTERED AT ALL.
     *
     * The year grid renders `getAllYearKeys(yearData)` and returns null for any slot with no
     * object, so a scheme whose parse began at year_2 shows Year 2, 3, 4 and NO Year 1 box —
     * there is nowhere to type the missing figures. Deleting was possible, renumbering was
     * possible, adding was not.
     *
     * Verified against the source PDFs for prod tender bd3f75f1 (WERRIBEE RACING CLUB): Flo's
     * environmental table opens with 01/09/2026-31/12/2026 (LGC 8.00 / 16.67% / 0.1334) and
     * Next Business opens with a CAL26 column, and BOTH parses skipped that first period on
     * every scheme. Renumbering year_2 -> year_1 does not help: it just relabels 2027's rate as
     * 2026's. The reviewer needs an empty slot to type the real values into.
     *
     * Lowest free slot, not "one past the end", precisely so a missing year_1 is what gets
     * created. Never overwrites an occupied slot, and adds nothing when the scheme is already
     * contiguous from 1 up to the cap.
     */
    const handleAddEnvYear = (chargeType: string) => {
        const envKey = getEnvironmentalChargesKey(activeRateData);
        const schemes = { ...(activeRateData?.[envKey] || {}) };
        const years = { ...(schemes[chargeType] || {}) };

        const slot = nextFreeEnvYearSlot(years);
        if (slot === null) return;

        // Match the shape the scheme's other years already use (snake_case vs camelCase), so
        // the new row's inputs bind to the same field names the rest of the offer was parsed
        // into. An empty {} would render but never receive a value.
        const sample = Object.values(years)[0] as Record<string, unknown> | undefined;
        const { rateKey, percentKey, certKey } = getEnvFieldKeys(sample);
        // The scheme's own other years are the best source for the new row's period; the snake
        // key is written whatever shape they use, because slotRange reads either spelling.
        const range = seedEnvYearRange(editedData, `year_${slot}`, years);
        years[`year_${slot}`] = {
            [rateKey]: null, [percentKey]: null, [certKey]: null,
            ...(range ? { date_range: range } : {}),
        };

        setEditedData({
            ...editedData,
            [activeRateSide]: {
                ...(editedData[activeRateSide] || {}),
                [envKey]: { ...schemes, [chargeType]: years },
            },
        });
    };

    /**
     * Renumber one Rate Year within a scheme — "Rate Year 1" becomes "Rate Year 2" and so on.
     *
     * Needed because a parse can land a scheme's periods on the wrong year slots (the whole
     * AGL off-by-one), and the reviewer has to be able to shift a year into its correct place
     * without retyping its rate, power % and certificate price.
     *
     * Refuses a target that is already occupied rather than overwriting it — the reviewer
     * should delete or move the occupant first, so no quoted figure is ever destroyed silently.
     * Returns false when the move was rejected so the caller can surface why.
     */
    const handleChangeEnvYearNumber = (chargeType: string, fromYear: string, toYearNum: number): boolean => {
        if (!Number.isInteger(toYearNum) || toYearNum < 1) return false;
        const toYear = `year_${toYearNum}`;
        if (toYear === fromYear) return true;

        const envKey = getEnvironmentalChargesKey(activeRateData);
        const schemes = { ...(activeRateData?.[envKey] || {}) };
        const years = { ...(schemes[chargeType] || {}) };
        if (!(fromYear in years)) return false;
        if (toYear in years) return false; // occupied — never clobber a quoted year

        years[toYear] = years[fromYear];
        delete years[fromYear];

        /*
         * ⚠️ THE ROW'S PERIOD DOES NOT FOLLOW ITS NEW NUMBER, AND THE POWER % FOLLOWS THE PERIOD.
         *
         * The move above carries the whole slot — rate, power %, certificate price AND its
         * date_range. That is right when the reviewer is correcting a slot NUMBER the parse got
         * wrong (the AGL off-by-one this control exists for): there the document's own period is
         * the trustworthy part and must survive.
         *
         * It is wrong when the reviewer is saying "this row IS Year 2". Reported on dev tender
         * 8cdb0db2: SmartestEnergy's LREC/SREC were renumbered year_1 -> year_2 and kept year 1's
         * window, 2027-02-01 -> 2028-01-31. resolvePowerPct reads the calendar year off that
         * start date, so the row went on resolving to 2027 — LREC 19.17%, SREC 12.92% — where
         * the client's model prices Year 2 at 18.98% and 9.27%. Nothing on screen shows a slot's
         * period, so the contradiction was invisible and the money was 27% high.
         *
         * Told apart by asking the two sources the same question. A period that still agrees with
         * the number it now carries is the document's and is left exactly alone — which is every
         * off-by-one correction, since there the date was right all along. Only a period that
         * CONTRADICTS the new number is stale, and it is rebased onto the offer's own contract
         * start (see envYearRangeFromTerms), never invented: with no dated term to derive from,
         * the slot keeps what it has rather than gaining a period nobody stated.
         */
        const moved = { ...(years[toYear] as Record<string, any>) };
        const rebased = rebasedEnvYearRange(moved, years, getContractTermsData(editedData), toYearNum);
        if (rebased) {
            // Written back under the spelling the slot already uses, so the shape is unchanged.
            const rangeKey = moved.dateRange !== undefined ? 'dateRange' : 'date_range';
            years[toYear] = { ...moved, [rangeKey]: rebased };
        }

        setEditedData({
            ...editedData,
            [activeRateSide]: {
                ...(editedData[activeRateSide] || {}),
                [envKey]: { ...schemes, [chargeType]: years },
            },
        });
        return true;
    };

    /**
     * Assign which contract year a term IS — "this 16-month term is Year 2".
     *
     * Unlike handleChangeEnvYearNumber this cannot move data between slots: 16 months is real
     * data and cannot be renumbered into 24. So the choice is stored as its own mapping and
     * every label resolves through it (termLabel.ts).
     *
     * Needed because no rule can decide it. WPG SYDNEY and GORKHA both quote 4/16/28/40/52
     * months; WPG's 16-month term is Year 1 and GORKHA's is Year 2. Only the reviewer knows.
     *
     * A term that is a whole number of years is never asked about — 12 is Year 1 and always
     * will be — so this only ever carries the unconventional ones.
     */
    const handleChangeTermYear = (termKey: string, yearNum: number): void => {
        if (!Number.isInteger(yearNum) || yearNum < 1) return;
        const months = parseInt(String(termKey).split('_')[0], 10);
        if (!isFinite(months) || months <= 0) return;

        const existing = { ...(getTermYearMapping(editedData) || {}) };
        // Keyed by month count, so a "48"/"48_stepped" pair shares one assignment.
        existing[String(months)] = yearNum;

        setEditedData({ ...editedData, [TERM_YEAR_MAPPING_KEY]: existing });
    };

    const handleAddContractTerm = (months: number, rateType: 'Smooth' | 'Stepped') => {
        const ctKey = getContractTermsKey(editedData);
        const existing = getContractTermsData(editedData) || {};
        // Always use variant key so it coexists with the other type
        const termKey = `${months}_${rateType.toLowerCase()}`;
        if (existing[termKey]) return; // already exists
        const years = getExpectedYearsForTerm(months);
        const emptyYears = Object.fromEntries(years.map(y => [y, {}]));
        const newData = {
            ...editedData,
            [ctKey]: { ...existing, [termKey]: { rate_type: rateType, ...emptyYears } },
        };
        setEditedData(newData);
        setAddTermPanel(null);
    };

    /**
     * Remove one rate year from a term.
     *
     * ⚠️ NOT A PLAIN DELETE. Dropping the year object alone leaves a hole at that slot, and
     * unpricedContractYears then omits the whole term from the comparison — Stepped terms get no
     * Year-1 substitute, because borrowing one would invent a quote the retailer never gave. The
     * offer would silently disappear from that term's table.
     *
     * removeTermYear renumbers the survivors, re-measures the term from the span they actually
     * cover (Origin's 51 months become 48 once its 3-month stub goes, not 39) and rebases the
     * dates. The contract_terms key is renamed to match, keeping the _smooth/_stepped suffix.
     */
    const handleRemoveRateYear = (termKey: string, yearKey: string) => {
        const ctKey = getContractTermsKey(editedData);
        const existing = { ...getContractTermsData(editedData) };
        const termData = existing[termKey];
        if (!termData) return;

        const months = parseInt(termKey.split('_')[0], 10);
        const result = removeTermYear(termData, months, yearKey);
        if (!result) {
            toast({
                title: 'Cannot remove this year',
                description: 'A term must keep at least one rate year.',
                variant: 'destructive',
            });
            return;
        }

        const suffix = termKey.indexOf('_') >= 0 ? termKey.slice(termKey.indexOf('_')) : '';
        const newKey = `${result.months}${suffix}`;
        // Never overwrite a term the retailer genuinely quoted at the new length.
        if (newKey !== termKey && existing[newKey] !== undefined) {
            toast({
                title: `A ${result.months}-month term already exists`,
                description: 'Remove or rename it first, or this offer would lose a quote.',
                variant: 'destructive',
            });
            return;
        }

        if (newKey !== termKey) delete existing[termKey];
        existing[newKey] = result.termData;
        setEditedData({ ...editedData, [ctKey]: existing });
        toast({
            title: `Removed ${formatRateYearLabel(parseInt(yearKey.slice(5), 10) || 1)}`,
            description: `Term is now ${result.months} months; later years moved up and the dates were rebased.`,
            duration: 5000,
        });
    };

    const handleDeleteTerm = (termKey: string) => {
        const ctKey = getContractTermsKey(editedData);
        const existing = { ...getContractTermsData(editedData) };
        delete existing[termKey];
        setEditedData({ ...editedData, [ctKey]: existing });
    };

    const handleModifyTerm = (oldKey: string, months: number, rateType: 'Smooth' | 'Stepped') => {
        const ctKey = getContractTermsKey(editedData);
        const existing = { ...getContractTermsData(editedData) };
        const oldData = existing[oldKey] || {};
        const newKey = `${months}_${rateType.toLowerCase()}`;
        // Pass oldData: re-terming a 40-month offer to 52 must not manufacture a year_5 the
        // retailer never quoted. Years already priced are carried over; nothing empty is added.
        const years = getExpectedYearsForTerm(months, oldData as Record<string, any>);
        const rebuilt: Record<string, unknown> = { rate_type: rateType };
        years.forEach(y => { rebuilt[y] = (oldData as Record<string, unknown>)[y] || {}; });
        if (newKey !== oldKey) delete existing[oldKey];
        // Rebase the quoted dates onto the new month count. Without this the term keeps the OLD
        // label's range — a 12-month range under a "4 month" key — and the engine's
        // label-corroboration guard (MAX_TERM_LABEL_DRIFT_DAYS = 15) throws those dates away,
        // silently dropping the term to the months-based approximation
        // `termMonths / 12 - 1/365` instead of its true day count / 365. On prod tender
        // bd3f75f1 that priced three re-termed retailers 0.28% under the client's model on
        // every figure. Only existing ranges are rewritten; none are invented.
        existing[newKey] = rebaseTermYearRanges(rebuilt, months);
        setEditedData({ ...editedData, [ctKey]: existing });
        setEditingTermKey(null);
        setEditTermConfig(null);
    };

    // Single source of truth for "save whatever's currently being edited" — used by Save &
    // Approve, the prev/next arrows below, AND the parent's own flushCurrentEdit handle. Every
    // path that switches currentOffer away from what's on screen MUST go through this first, or
    // that offer's in-progress edit is silently lost the instant currentOffer changes (the load
    // effect resets editedData to the new offer's data with no memory of the old one).
    const flushEdit = async (): Promise<{ offerId: string; editedData: any } | undefined> => {
        if (!currentOffer) return undefined;
        const dataToSave = getDisplayRateType(editedData) === 'Mixed'
            ? { ...editedData, selectedRateType: selectedRateFilter }
            : editedData;
        // ⚠️ SAVE, DO NOT APPROVE. This runs on every way OUT of an offer — clicking another
        // retailer in the list, the prev/next arrows, leaving step 3 via the stepper. It used to
        // call onApprove, so simply looking at an offer and clicking away stamped it `reviewed`
        // with a reviewedAt and pushed it to the DB. Browsing seven retailers once marked all
        // seven reviewed without a single figure having been checked.
        //
        // Approval stays where the reviewer puts it: the Save & Approve button below.
        await (onSaveEdits ?? onApprove)(currentOffer.id, dataToSave);
        return { offerId: currentOffer.id, editedData: dataToSave };
    };

    const handleSaveAndApprove = async () => {
        if (!currentOffer) return;
        const dataToSave = getDisplayRateType(editedData) === 'Mixed'
            ? { ...editedData, selectedRateType: selectedRateFilter }
            : editedData;
        // The one place that marks an offer reviewed. Deliberately not routed through flushEdit
        // any more — that saves without approving, which is what every navigation path needs.
        await onApprove(currentOffer.id, dataToSave);
        if (currentIndex < offers.length - 1) {
            onNavigate(currentIndex + 1);
        }
    };

    // Lets the parent's own "Next" button (and its own offer-list clicks) capture the
    // currently-viewed offer's in-progress edit without requiring "Save & Approve" to be clicked
    // first — just doesn't also auto-advance, since the caller is navigating away anyway.
    useImperativeHandle(ref, () => ({
        flushCurrentEdit: flushEdit,
    }), [currentOffer, editedData, selectedRateFilter, onApprove, onSaveEdits]);

    if (!currentOffer) {
        return (
            <div className="flex items-center justify-center h-full text-muted-foreground">
                No offers to review
            </div>
        );
    }

    const isReviewed = currentOffer.status === 'reviewed';
    const hasChanges = JSON.stringify(editedData) !== JSON.stringify(currentOffer.parsedData);

    // Contract years this offer has assigned to more than one term. Computed from EVERY term
    // the offer holds, not just the visible tab set, so filtering the view to Smooth or Stepped
    // cannot hide a clash and let a broken offer through.
    const termYearConflicts = duplicateTermYears(
        Object.keys(getContractTermsData(editedData) || {}),
        getTermYearMapping(editedData),
    );

    return (
        <div className="flex flex-col gap-4 p-4">
            {/* Header with Navigation */}
            <div className="flex-shrink-0 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-semibold">{currentOffer.fileName}</h2>
                    </div>
                    <Badge variant={isReviewed ? "default" : "secondary"}>
                        {isReviewed ? (
                            <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Reviewed
                            </>
                        ) : (
                            "Pending Review"
                        )}
                    </Badge>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                        {currentIndex + 1} of {offers.length}
                    </span>
                    <div className="flex gap-1">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => { await flushEdit(); onNavigate(currentIndex - 1); }}
                            disabled={currentIndex === 0}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => { await flushEdit(); onNavigate(currentIndex + 1); }}
                            disabled={currentIndex === offers.length - 1}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content: Side-by-side view */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left: PDF Viewer */}
                <Card className="flex flex-col overflow-hidden min-h-[450px]">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">
                                Original Document
                            </CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative">
                        <div className="h-full bg-gray-50 flex items-center justify-center relative">
                            {loadingPdf ? (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                                    <p className="text-sm text-muted-foreground">Loading PDF...</p>
                                </div>
                            ) : pdfError ? (
                                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                    <FileText className="h-16 w-16 mb-4 opacity-20" />
                                    <p className="text-sm text-red-600 mb-4">{pdfError}</p>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={loadPdf}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            ) : pdfUrl && isPdfDocument ? (
                                <iframe
                                    src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                                    className="w-full h-full border-0 absolute inset-0"
                                    title={currentOffer.fileName}
                                />
                            ) : pdfUrl ? (
                                // A spreadsheet or Word document. The browser cannot show it in a
                                // frame, and a blank frame reads as a broken page — so say what it
                                // is and hand it over instead.
                                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                    <FileText className="h-16 w-16 mb-4 opacity-20" />
                                    <p className="text-sm mb-1">This document cannot be previewed in the browser.</p>
                                    <p className="text-xs text-gray-500 mb-4">{currentOffer.fileName}</p>
                                    <Button size="sm" variant="outline" asChild>
                                        <a href={pdfUrl} download={currentOffer.fileName}>
                                            Download to view
                                        </a>
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                    <FileText className="h-16 w-16 mb-4 opacity-20" />
                                    <p className="text-sm">PDF preview not available</p>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Document: {currentOffer.fileName}
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Right: Editable Parsed Data */}
                <Card className="flex flex-col overflow-hidden min-h-[450px]">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">
                                Extracted Data
                            </CardTitle>
                            <Button
                                size="sm"
                                variant={isEditing ? "default" : "outline"}
                                onClick={() => {
                                    if (!isEditing) setEditedData((prev: any) => bakeEnvironmentalDerivedValues(prev));
                                    setIsEditing(!isEditing);
                                }}
                            >
                                {isEditing ? (
                                    <>
                                        <Eye className="h-3 w-3 mr-1" />
                                        View Mode
                                    </>
                                ) : (
                                    <>
                                        <Edit3 className="h-3 w-3 mr-1" />
                                        Edit Mode
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 overflow-y-auto" style={{ maxHeight: '400px' }}>
                        <div className="px-6">
                            <div className="space-y-6 py-4">
                                {/* Offer Expiry Date */}
                                <div>
                                    <Label className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        Offer Validity / Expiry Date
                                    </Label>
                                    <div className="mt-2">
                                        <Input
                                            type="date"
                                            value={getExpiryDate(editedData) || ''}
                                            onChange={(e) => handleFieldChange([getExpiryDateKey(editedData)], e.target.value)}
                                            disabled={!isEditing}
                                            className="h-8 text-sm w-48"
                                        />
                                        {!getExpiryDate(editedData) && (
                                            <p className="text-xs text-muted-foreground mt-1">No expiry date specified in offer</p>
                                        )}
                                    </div>
                                </div>

                                <Separator />

                                {/* Rate Type — determined from extracted data */}
                                <div>
                                    <Label className="text-xs font-semibold text-muted-foreground uppercase">
                                        Rate Type
                                    </Label>
                                    <div className="flex gap-2 mt-2 flex-wrap items-center">
                                        {(() => {
                                            const rt = getDisplayRateType(editedData);
                                            if (rt === 'Mixed') {
                                                return (
                                                    <>
                                                        <Badge variant="secondary">Mixed</Badge>
                                                        <span className="text-xs text-muted-foreground self-center">(Both Smooth &amp; Stepped available)</span>
                                                        <span className="mx-1 text-muted-foreground">→</span>
                                                        <Badge
                                                            className={selectedRateFilter === 'Smooth'
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-amber-500 text-white'}
                                                        >
                                                            {selectedRateFilter} selected
                                                        </Badge>
                                                    </>
                                                );
                                            }
                                            const variant = rt === 'Stepped' ? 'destructive' : 'default';
                                            const desc = rt === 'Stepped' ? 'Year-by-year rates' : 'Single blended rate for full term';
                                            return (
                                                <>
                                                    <Badge variant={variant as any}>{rt}</Badge>
                                                    <span className="text-xs text-muted-foreground self-center">({desc})</span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <Separator />

                                {/* Render contract term based data (NEW FORMAT) */}
                                {isNewContractTermsFormat(editedData) && (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-semibold text-sm flex items-center gap-2">
                                                <div className="h-1 w-1 rounded-full bg-primary" />
                                                Contract Term Rates
                                                <Badge variant="outline" className="ml-2 text-xs">
                                                    {(getDisplayRateType(editedData) === 'Mixed'
                                                        ? getFilteredContractTerms(editedData, selectedRateFilter)
                                                        : getAvailableContractTerms(editedData)
                                                    ).length} contract term(s)
                                                </Badge>
                                            </h3>

                                            <div className="flex items-center gap-2">
                                                {/* Section reparse buttons */}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 px-2 text-xs text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                                    disabled={!!reparsingSections['energy_rates']}
                                                    onClick={() => handleSectionReparse('energy_rates')}
                                                >
                                                    {reparsingSections['energy_rates']
                                                        ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                        : <RefreshCw className="h-3 w-3 mr-1" />
                                                    }
                                                    Reparse Energy
                                                </Button>
                                                <div className="w-px h-4 bg-border mx-1" />

                                                {/* Smooth / Stepped toggle — only shown for Mixed offers */}
                                                {getDisplayRateType(editedData) === 'Mixed' && (
                                                    <>
                                                        <div className="flex items-center gap-1 bg-white border-2 border-gray-300 rounded-lg p-0.5 shadow-sm">
                                                            {(['Smooth', 'Stepped'] as const).map(type => (
                                                                <button
                                                                    key={type}
                                                                    onClick={() => setSelectedRateFilter(type)}
                                                                    className={[
                                                                        'px-3 py-1.5 rounded text-xs font-semibold transition-all',
                                                                        selectedRateFilter === type
                                                                            ? type === 'Smooth'
                                                                                ? 'bg-blue-600 text-white shadow-sm'
                                                                                : 'bg-amber-500 text-white shadow-sm'
                                                                            : 'text-gray-500 hover:bg-gray-100',
                                                                    ].join(' ')}
                                                                >
                                                                    {type}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        <div className="w-px h-4 bg-border mx-1" />
                                                    </>
                                                )}

                                                {/* View Mode Switcher */}
                                                <Button
                                                    size="sm"
                                                    variant={contractTermsViewMode === 'terms' ? 'default' : 'outline'}
                                                    onClick={() => setContractTermsViewMode('terms')}
                                                    className="h-7 text-xs"
                                                >
                                                    <Table2 className="h-3 w-3 mr-1" />
                                                    By Terms
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant={contractTermsViewMode === 'rates' ? 'default' : 'outline'}
                                                    onClick={() => setContractTermsViewMode('rates')}
                                                    className="h-7 text-xs"
                                                >
                                                    <LayoutGrid className="h-3 w-3 mr-1" />
                                                    By Rates
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Term Management Bar — edit mode only */}
                                        {isEditing && (
                                            <div className="flex flex-wrap gap-2 items-center p-2.5 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mr-1">Terms:</span>
                                                {(() => {
                                                // Chip labels come from the whole series too, or the delete/rename row
                                                // shows two "Year 4"s and no Year 5 — the same collision the tab strip
                                                // had. See formatTermSeriesLabels.
                                                const allTermKeys = getAvailableContractTerms(editedData);
                                                const chipLabels = formatTermSeriesLabels(allTermKeys, getTermYearMapping(editedData));
                                                return allTermKeys.map(term => {
                                                    const parts = term.split('_');
                                                    const tMonths = parseInt(parts[0], 10);
                                                    // ⚠️ THE KEY'S SUFFIX IS NOT THE ONLY PLACE THE RATE TYPE LIVES.
                                                    //
                                                    // Plenty of offers store a single term under a bare month key —
                                                    // "51", not "51_stepped" — and carry the rate type in the term's own
                                                    // `rate_type` field. Reading the suffix alone made every such term
                                                    // read "Smooth", including Origin's 51-month STEPPED offer whose own
                                                    // badge says Stepped two lines above. Saving the pre-filled value
                                                    // then rewrote a stepped offer's key to `51_smooth` AND set
                                                    // rate_type: 'Smooth' — silently repricing five escalating years as
                                                    // one flat rate, with nothing on screen to show it had happened.
                                                    const tData = getContractTermsData(editedData)?.[term];
                                                    const tType: 'Smooth' | 'Stepped' =
                                                        parts[1] === 'stepped' ? 'Stepped'
                                                        : parts[1] === 'smooth' ? 'Smooth'
                                                        : (tData?.rate_type === 'Stepped' || tData?.rateType === 'Stepped')
                                                            ? 'Stepped' : 'Smooth';
                                                    const isEditingThis = editingTermKey === term;
                                                    return isEditingThis && editTermConfig ? (
                                                        <div key={term} className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1 shadow-sm">
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                max={240}
                                                                className="text-xs border-0 outline-none bg-transparent w-10 text-center"
                                                                value={editTermConfig.months}
                                                                onChange={e => setEditTermConfig({ ...editTermConfig, months: Number(e.target.value) })}
                                                            />
                                                            <span className="text-xs text-gray-500">m</span>
                                                            <select
                                                                className="text-xs border-0 outline-none bg-transparent"
                                                                value={editTermConfig.rateType}
                                                                onChange={e => setEditTermConfig({ ...editTermConfig, rateType: e.target.value as 'Smooth' | 'Stepped' })}
                                                            >
                                                                <option value="Smooth">Smooth</option>
                                                                <option value="Stepped">Stepped</option>
                                                            </select>
                                                            <button
                                                                onClick={() => handleModifyTerm(term, editTermConfig.months, editTermConfig.rateType)}
                                                                className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded hover:bg-blue-700"
                                                            >Save</button>
                                                            <button onClick={() => { setEditingTermKey(null); setEditTermConfig(null); }} className="text-gray-400 hover:text-gray-600">
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div key={term} className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-1 text-xs shadow-sm">
                                                            <span className="font-medium text-gray-700">{chipLabels.get(term) ?? formatContractTermLabel(term)}</span>
                                                            <button
                                                                onClick={() => { setEditingTermKey(term); setEditTermConfig({ months: tMonths, rateType: tType }); }}
                                                                className="text-gray-400 hover:text-blue-600 ml-1"
                                                                title="Rename term"
                                                            >
                                                                <Edit3 className="h-3 w-3" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTerm(term)}
                                                                className="text-gray-400 hover:text-red-500"
                                                                title="Delete term"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                });
                                                })()}
                                                {/* Add new term */}
                                                {addTermPanel ? (
                                                    <div className="flex items-center gap-1.5 bg-white border border-green-300 rounded-md px-2 py-1 shadow-sm">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={240}
                                                            className="text-xs border-0 outline-none bg-transparent w-10 text-center"
                                                            value={addTermPanel.months}
                                                            onChange={e => setAddTermPanel({ ...addTermPanel, months: Number(e.target.value) })}
                                                        />
                                                        <span className="text-xs text-gray-500">m</span>
                                                        <select
                                                            className="text-xs border-0 outline-none bg-transparent"
                                                            value={addTermPanel.rateType}
                                                            onChange={e => setAddTermPanel({ ...addTermPanel, rateType: e.target.value as 'Smooth' | 'Stepped' })}
                                                        >
                                                            <option value="Smooth">Smooth</option>
                                                            <option value="Stepped">Stepped</option>
                                                        </select>
                                                        <button
                                                            onClick={() => handleAddContractTerm(addTermPanel.months, addTermPanel.rateType)}
                                                            className="text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded hover:bg-green-700"
                                                        >Add</button>
                                                        <button onClick={() => setAddTermPanel(null)} className="text-gray-400 hover:text-gray-600">
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setAddTermPanel({ months: 24, rateType: 'Smooth' })}
                                                        className="flex items-center gap-1 text-xs text-blue-600 border border-dashed border-blue-300 rounded-md px-2 py-1 hover:bg-blue-50"
                                                    >
                                                        <Plus className="h-3 w-3" /> Add Term
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {/* Inline loading card — replaces tabs only, NEC/Env stay visible */}
                                        {reparsingSections['energy_rates'] && (
                                            <div className="flex flex-col items-center justify-center py-12 gap-3 bg-muted/10 rounded-xl border border-dashed">
                                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                <p className="text-sm font-semibold">Reparsing Energy Rates…</p>
                                                <p className="text-xs text-muted-foreground">AI is extracting rates from the document</p>
                                                <Button size="sm" variant="outline" className="mt-1" onClick={() => handleCancelSection('energy_rates')}>
                                                    <X className="h-3 w-3 mr-1" />Cancel
                                                </Button>
                                            </div>
                                        )}

                                        {/* Contract Terms View — one FLAT top-level tab per Agreement Year (same tab bar
                                            level as Smooth's term tabs, not nested). Tab labels are always
                                            month-based ("12 Months", "24 Months"), never "Year N". Only a
                                            SINGLE contract term with multiple Agreement Years gets split into
                                            one tab per year (e.g. MAWHOOD's one 24-month deal, Year1+Year2) —
                                            genuinely separate terms (e.g. ENGIE's 12/24/36/48 options) stay as
                                            one tab per term; splitting those again by year would duplicate the
                                            same underlying years across multiple term tabs. */}
                                        {!reparsingSections['energy_rates'] && contractTermsViewMode === 'terms' && (() => {
                                            const isMixed = getDisplayRateType(editedData) === 'Mixed';
                                            const visibleTerms = isMixed
                                                ? getFilteredContractTerms(editedData, selectedRateFilter)
                                                : getAvailableContractTerms(editedData);
                                            const contractTerms = getContractTermsData(editedData);
                                            const ctKey = getContractTermsKey(editedData);

                                            /**
                                             * A priced period INSIDE one term — "Rate Year 1", "Rate Year 2".
                                             *
                                             * ⚠️ DELIBERATELY NOT "Year 1". THE SCREEN ALREADY HAS ONE OF THOSE.
                                             *
                                             * Two different things were both called "Year N" here. The TERM's year
                                             * says which of the tender's contract options this is — the chip above,
                                             * and what the dropdown sets. The ROWS are the years priced within that
                                             * term. A 24-month term is the tender's Year 2 AND holds two rate years,
                                             * so the screen showed "Year 1" and "Year 2" tabs under a "Year 2" chip,
                                             * with a header reading "Year 1 Contract Rates is Year 2" — a sentence
                                             * that contradicts itself while describing perfectly consistent data.
                                             *
                                             * The rows get the qualifier because they are the nested concept; the
                                             * term keeps the plain name it shares with the tender's own terms.
                                             */
                                            const monthsLabel = (_termData: any, yearKey: string): string =>
                                                `Rate ${formatRateYearLabel(parseInt(yearKey.split('_')[1], 10) || 1)}`;

                                            const flatTabs = visibleTerms.length === 1
                                                ? (() => {
                                                    const term = visibleTerms[0];
                                                    const termData = contractTerms?.[term];
                                                    const expectedYears = getEditableYearsForTerm(term, termData);
                                                    return expectedYears.map((yearKey) => ({
                                                        key: `${term}::${yearKey}`,
                                                        term,
                                                        termData,
                                                        years: [yearKey],
                                                        label: monthsLabel(termData, yearKey),
                                                    }));
                                                })()
                                                : (() => {
                                                    // Labelled from the whole series, not term by term. This offer quotes
                                                    // 12/24/36/48/53 months — the last running to a calendar year end — and
                                                    // 53 snaps nearer 48 than 60, so per-term labelling produced two
                                                    // "Year 4" tabs with the fifth year's rates under the fourth.
                                                    // See formatTermSeriesLabels.
                                                    const seriesLabels = formatTermSeriesLabels(visibleTerms, getTermYearMapping(editedData));
                                                    return visibleTerms.map((term) => {
                                                        const termData = contractTerms?.[term];
                                                        return {
                                                            key: term,
                                                            term,
                                                            termData,
                                                            years: getEditableYearsForTerm(term, termData),
                                                            label: seriesLabels.get(term) ?? formatContractTermLabel(term),
                                                        };
                                                    });
                                                })();

                                            const renderRateRow = (term: string, termData: any, yearKey: string, rowLabel: string) => {
                                                const isSmooth = !term.endsWith('_stepped') && termData?.rate_type !== 'Stepped';
                                                const year1Data = isSmooth ? (termData?.['year_1'] || {}) : {};
                                                const yearData = termData?.[yearKey] || {};
                                                const peakValue = yearData.peak ?? (yearKey !== 'year_1' && isSmooth ? (year1Data.peak ?? '') : '');
                                                const offpeakValue = yearData.offpeak ?? (yearKey !== 'year_1' && isSmooth ? (year1Data.offpeak ?? '') : '');
                                                const shoulderValue = yearData.shoulder ?? (yearKey !== 'year_1' && isSmooth ? (year1Data.shoulder ?? '') : '');
                                                const hasShoulderValue = shoulderValue !== null && shoulderValue !== undefined && shoulderValue !== '';
                                                const yearDateRange = formatDateRange(getDateRange(yearData));
                                                const yearDrKeys = getDateRangeKeys(yearData);

                                                return (
                                                    <div key={yearKey} className="grid grid-cols-5 gap-2 py-2 border-t border-muted/30 first:border-t-0">
                                                        <div className="flex items-center gap-1">
                                                            <Label className="text-sm font-medium">{rowLabel}</Label>
                                                            {/* Remove this rate year. Offered only while editing and only
                                                                when the term has more than one year to give — the last one
                                                                cannot go. Origin's 3-month stub first period is the case
                                                                this exists for: the tender never asked for it, and there
                                                                was no way to drop it without deleting the whole term. */}
                                                            {isEditing && getEditableYearsForTerm(
                                                                term, termData,
                                                            ).length > 1 && (
                                                                <Button
                                                                    variant="ghost" size="sm"
                                                                    className="h-5 w-5 p-0 shrink-0 text-gray-400 hover:text-destructive"
                                                                    title={`Remove ${rowLabel} — later years move up and the term is re-measured`}
                                                                    onClick={() => handleRemoveRateYear(term, yearKey)}
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <Input
                                                            type="number"
                                                            value={peakValue}
                                                            onChange={(e) => handleRateChange(
                                                                term, termData, yearKey, 'peak',
                                                                e.target.value ? parseFloat(e.target.value) : null
                                                            )}
                                                            disabled={!isEditing}
                                                            className="h-8 text-sm"
                                                            step={ENERGY_RATE_STEP}
                                                            placeholder="0.00"
                                                        />
                                                        <Input
                                                            type="number"
                                                            value={offpeakValue}
                                                            onChange={(e) => handleRateChange(
                                                                term, termData, yearKey, 'offpeak',
                                                                e.target.value ? parseFloat(e.target.value) : null
                                                            )}
                                                            disabled={!isEditing}
                                                            className="h-8 text-sm"
                                                            step={ENERGY_RATE_STEP}
                                                            placeholder="0.00"
                                                        />
                                                        <Input
                                                            type="number"
                                                            value={shoulderValue}
                                                            onChange={(e) => handleRateChange(
                                                                term, termData, yearKey, 'shoulder',
                                                                e.target.value ? parseFloat(e.target.value) : null
                                                            )}
                                                            disabled={!isEditing}
                                                            className={`h-8 text-sm ${!hasShoulderValue ? 'bg-amber-50 border-amber-200' : ''}`}
                                                            step={ENERGY_RATE_STEP}
                                                            placeholder={isNSW ? String(peakValue || '0.00') : '0'}
                                                        />
                                                        {isEditing ? (
                                                            <div className="flex gap-1 items-center">
                                                                <Input
                                                                    type="date"
                                                                    value={getDateRange(yearData)?.startDate || ''}
                                                                    onChange={(e) => handleFieldChange(
                                                                        [ctKey, term, yearKey, yearDrKeys.dateRangeKey, yearDrKeys.startKey],
                                                                        e.target.value || null
                                                                    )}
                                                                    className="h-7 text-xs w-24"
                                                                    placeholder="Start"
                                                                />
                                                                <span className="text-xs">-</span>
                                                                <Input
                                                                    type="date"
                                                                    value={getDateRange(yearData)?.endDate || ''}
                                                                    onChange={(e) => handleFieldChange(
                                                                        [ctKey, term, yearKey, yearDrKeys.dateRangeKey, yearDrKeys.endKey],
                                                                        e.target.value || null
                                                                    )}
                                                                    className="h-7 text-xs w-24"
                                                                    placeholder="End"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground flex items-center">{yearDateRange || '-'}</span>
                                                        )}
                                                    </div>
                                                );
                                            };

                                            return (
                                        <Tabs key={selectedRateFilter} defaultValue={flatTabs[0]?.key || "12"} className="w-full">
                                            {/* Many tabs don't fit an equal-width grid — scroll horizontally with
                                                natural tab widths instead of squishing/cutting off labels. */}
                                            <TabsList className="flex w-full justify-start overflow-x-auto flex-nowrap h-auto">
                                                {flatTabs.map((tab) => {
                                                    /* When the offer quotes ONE term the tabs are its
                                                       YEARS, and dropping a year was only possible from
                                                       the small x beside the rate row inside the tab —
                                                       you had to open the year to find out you could
                                                       remove it. Origin's 51-month schedule opens on
                                                       Year 1 with five year tabs above it, and the
                                                       reviewer who wants Year 1 gone is looking at the
                                                       tabs. So the same action is offered there too. It
                                                       is the same handler, so both behave identically:
                                                       later years move up and the term is re-measured.

                                                       Never on the last remaining year, and never while
                                                       the tabs are TERMS rather than years — deleting a
                                                       whole term is a different act with its own
                                                       control. */
                                                    const yearKey = tab.years.length === 1 && tab.key.includes('::')
                                                        ? tab.years[0] : null;
                                                    const canRemove = isEditing && yearKey !== null && flatTabs.length > 1;
                                                    return (
                                                    <TabsTrigger key={tab.key} value={tab.key} className="text-xs whitespace-nowrap shrink-0">
                                                        {tab.label}
                                                        {canRemove && (
                                                            /* A span, not a Button: TabsTrigger is itself
                                                               a button and nesting one is invalid HTML. */
                                                            <span
                                                                role="button"
                                                                tabIndex={-1}
                                                                aria-label={`Remove ${tab.label}`}
                                                                title={`Remove ${tab.label} — later years move up and the term is re-measured`}
                                                                className="ml-1.5 -mr-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded text-gray-400 hover:text-destructive"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleRemoveRateYear(tab.term, yearKey!);
                                                                }}
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </span>
                                                        )}
                                                    </TabsTrigger>
                                                    );
                                                })}
                                            </TabsList>

                                            {flatTabs.map(({ key, term, termData, years, label }) => (
                                                <TabsContent key={key} value={key} className="space-y-4 mt-4">
                                                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                                                        <Calendar className="h-3 w-3" />
                                                        <span>{formatDateRange(getTermDateRange(termData)) || 'No date range specified'}</span>
                                                    </div>

                                                    <div className="border rounded-lg overflow-hidden">
                                                        <div className="bg-muted/50 px-4 py-2 border-b flex items-center gap-3 flex-wrap">
                                                            {/* Just the tab's own name. It used to read "{label} Contract
                                                                Rates" beside an "is [Year N]" select, which on a single-term
                                                                offer produced "Year 1 Contract Rates is Year 2" — the row's
                                                                year and the term's year in one sentence, contradicting each
                                                                other. The rows now say "Rate Year N" and the select is
                                                                labelled for the term, so the two never collide. */}
                                                            <Label className="text-sm font-semibold">{label}</Label>
                                                            {/* Which contract year this term IS. Only offered for terms that are
                                                                not a whole number of years — 12 is Year 1 and always will be, so
                                                                asking would be busywork. No rule can decide the rest: WPG's
                                                                16-month term is Year 1 and GORKHA's is Year 2, same month count.
                                                                Only free years are listed, so a choice can never collide. */}
                                                            {(() => {
                                                                const mapping = getTermYearMapping(editedData);
                                                                const months = parseInt(term, 10);
                                                                /**
                                                                 * What this select shows must be the TERM's assigned year — the
                                                                 * same thing changing it writes.
                                                                 *
                                                                 * ⚠️ THE LABEL IS NOT ALWAYS THAT.
                                                                 *
                                                                 * With several terms on screen each tab IS a term, and its label
                                                                 * already comes from this mapping, so reading the label agreed.
                                                                 * With ONE term the tabs are that term's YEAR ROWS instead, and
                                                                 * the label is the row's own year — derived from the dates and
                                                                 * blind to the mapping. So the select displayed "Year 1" (the
                                                                 * row) while writing the term's assignment, and every choice
                                                                 * snapped straight back: picking Year 2 stored it, the re-render
                                                                 * recomputed the same row label, and nothing on screen moved.
                                                                 *
                                                                 * Seen after deleting a year from a two-year term: removeTermYear
                                                                 * renumbers year_2 down to year_1, correctly, and the reviewer
                                                                 * then had no way to say the term is still Year 2.
                                                                 *
                                                                 * The offer's own assignment wins over the tender-wide one, which
                                                                 * is the order formatTermSeriesLabels uses for the many-term case.
                                                                 */
                                                                const assignedYear = resolveAssignedTermYear(mapping, months);
                                                                const current = String(assignedYear ?? (label.match(/Year (\d+)/)?.[1] ?? ''));
                                                                // Flagged only while an UNCONVENTIONAL term is still deriving its
                                                                // year. A whole number of years answers itself (12 is Year 1), so
                                                                // it is shown as assignable but never nagged about.
                                                                // ...or by the TENDER's term series, set once in the panel at the
                                                                // top of this step. Without that second test every unconventional
                                                                // term on every offer read "needs mapping" even on a tender whose
                                                                // series already states what Year each one is — 28 false nags on a
                                                                // 7-offer, 4-term tender, which trains the reviewer to ignore the
                                                                // badge on the offer that genuinely does need one.
                                                                const needsMapping = termNeedsYearMapping(term)
                                                                    && !normaliseTermYearMapping(mapping).has(months)
                                                                    && !getActiveTermYearMapping().has(months);
                                                                return (
                                                                    <div className="flex items-center gap-1.5">
                                                                        {/* "Term is", not "is" — it sets which of the TENDER's
                                                                            contract years this whole term is, not the year of the
                                                                            row on screen. */}
                                                                        <span className="text-xs text-muted-foreground">Term is</span>
                                                                        {/* Rendered on EVERY term and merely disabled outside edit
                                                                            mode, like every other control on this screen. Wrapping
                                                                            it in `isEditing &&` removed it from the DOM entirely,
                                                                            so there was nothing to see before clicking Edit and no
                                                                            hint the control existed. */}
                                                                        <select
                                                                            value={current}
                                                                            disabled={!isEditing}
                                                                            onChange={(e) => handleChangeTermYear(term, parseInt(e.target.value, 10))}
                                                                            title={`Set which contract year the ${term.split('_')[0]}-month term is`}
                                                                            className="h-7 text-xs border rounded px-1 bg-background disabled:opacity-60"
                                                                        >
                                                                            {termYearChoices().map(y => (
                                                                                <option key={y} value={y}>Year {y}</option>
                                                                            ))}
                                                                        </select>
                                                                        {needsMapping && (
                                                                            <span className="text-xs text-amber-600 font-medium">needs mapping</span>
                                                                        )}
                                                                        {/* The whole-of-term multiplier this term will actually be
                                                                            priced with, and the day count it comes from.
                                                                            ⚠️ READ-ONLY BUT LOAD-BEARING. Every dollar on this term
                                                                            is an ANNUAL figure times this number, and it is derived
                                                                            from the date range on the year rows below — which ARE
                                                                            editable. Until this was shown there was no way to see
                                                                            what the engine would use, so a one-day difference in a
                                                                            parsed end date moved a term total silently.
                                                                            WERRIBEE's 16-month term is the case in point: the
                                                                            client's model priced it over 485 days where the parsed
                                                                            dates give 486 — a 0.21% gap on every retailer that
                                                                            nobody could see or correct. With the span on screen the
                                                                            reviewer can compare it against the client's own figure
                                                                            and adjust the end date if they disagree. */}
                                                                        {(() => {
                                                                            const days = termSpanDaysFromDates(termData, months);
                                                                            if (days === null) return null;
                                                                            return (
                                                                                <span
                                                                                    className="text-xs text-muted-foreground font-mono border rounded px-1.5 py-0.5"
                                                                                    title={`Priced over ${days} days (leap days excluded) = a whole-of-term multiplier of ${(days / 365).toFixed(6)}. Taken from the date range on the rows below — edit those dates to change it.`}
                                                                                >
                                                                                    {days} days
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                        <div className="overflow-x-auto">
                                                        <div className="p-4 min-w-[620px]">
                                                            <div className="grid grid-cols-5 gap-2 mb-2">
                                                                <div className="text-xs font-medium text-muted-foreground">Period</div>
                                                                <div className="text-xs font-medium text-muted-foreground">Peak (c/kWh)</div>
                                                                <div className="text-xs font-medium text-muted-foreground">Off-Peak (c/kWh)</div>
                                                                <div className="text-xs font-medium text-muted-foreground">Shoulder (c/kWh)</div>
                                                                <div className="text-xs font-medium text-muted-foreground">Date Range</div>
                                                            </div>
                                                            {years.map((yearKey) => renderRateRow(term, termData, yearKey, monthsLabel(termData, yearKey)))}
                                                        </div>
                                                        </div>
                                                    </div>
                                                </TabsContent>
                                            ))}
                                        </Tabs>
                                        );
                                        })()}

                                        {/* Rate Type View */}
                                        {!reparsingSections['energy_rates'] && contractTermsViewMode === 'rates' && (
                                            <div className="space-y-4">
                                                {['peak', 'offpeak', 'shoulder'].map((rateType) => {
                                                    const rateTypeKey = rateType as 'peak' | 'offpeak' | 'shoulder';
                                                    const rateTypeLabel = rateType === 'peak' ? 'Peak' : rateType === 'offpeak' ? 'Off-Peak' : 'Shoulder';
                                                    const contractTermsData = getContractTermsData(editedData);
                                                    const rateData = getRateTypeViewData(contractTermsData).filter(d => d.rateType === rateTypeKey);
                                                    const ctKey = getContractTermsKey(editedData);

                                                    if (rateData.length === 0) return null;

                                                    return (
                                                        <div key={rateType} className="border rounded-lg overflow-hidden">
                                                            <div className="bg-muted/50 px-4 py-2 border-b">
                                                                <Label className="text-sm font-semibold">
                                                                    {rateTypeLabel} Rates (c/kWh)
                                                                </Label>
                                                            </div>
                                                            <div className="overflow-x-auto">
                                                            <div className="p-4 min-w-[500px]">
                                                                <div className="grid grid-cols-4 gap-2 mb-2">
                                                                    <div className="text-xs font-medium text-muted-foreground">Contract Term</div>
                                                                    <div className="text-xs font-medium text-muted-foreground">Year Range</div>
                                                                    <div className="text-xs font-medium text-muted-foreground">Rate (c/kWh)</div>
                                                                    <div className="text-xs font-medium text-muted-foreground">Date Range</div>
                                                                </div>

                                                                {rateData.map((item, idx) => {
                                                                    const termData = contractTermsData?.[item.term];
                                                                    return (
                                                                    <div key={idx} className="space-y-2">
                                                                        {/* Contract Term Header Row */}
                                                                        <div className="grid grid-cols-4 gap-2 py-2 border-t border-muted/30 bg-muted/10 rounded px-2">
                                                                            <div className="flex items-center text-sm font-semibold">
                                                                                {formatContractTermLabel(item.term)}
                                                                            </div>
                                                                            <div className="flex items-center text-sm font-medium">
                                                                                {item.yearRange}
                                                                            </div>
                                                                            <div className="flex items-center text-xs text-muted-foreground">
                                                                                {item.values.length} year(s)
                                                                            </div>
                                                                            <div className="flex items-center text-xs text-muted-foreground">
                                                                                {/* Contract term date range if available */}
                                                                                {formatDateRange(getDateRange(termData)) || '-'}
                                                                            </div>
                                                                        </div>

                                                                        {/* Individual Year Rows (indented) */}
                                                                        {item.values.map((yearValue, yearIdx) => {
                                                                            const yearData = termData?.[yearValue.yearKey];
                                                                            const yearDrKeys = getDateRangeKeys(yearData);
                                                                            return (
                                                                            <div key={yearIdx} className="grid grid-cols-4 gap-2 py-1 pl-6 border-l-2 border-muted/30">
                                                                                <div className="flex items-center text-xs text-muted-foreground">
                                                                                    {/* Empty - indented */}
                                                                                </div>
                                                                                <div className="flex items-center text-xs font-medium">
                                                                                    {formatYearLabel(yearValue.yearKey)}
                                                                                </div>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={yearValue.value ?? ''}
                                                                                    onChange={(e) => {
                                                                                        handleFieldChange(
                                                                                            [ctKey, item.term, yearValue.yearKey, rateTypeKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        );
                                                                                    }}
                                                                                    disabled={!isEditing}
                                                                                    className={`h-7 text-xs ${rateTypeKey === 'shoulder' && yearValue.value === null ? 'bg-amber-50 border-amber-200' : ''}`}
                                                                                    step={ENERGY_RATE_STEP}
                                                                                    placeholder={rateTypeKey === 'shoulder' ? (isNSW ? String(yearData?.peak ?? '0.00') : '0') : '0.00'}
                                                                                />
                                                                                <div className="flex items-center">
                                                                                    {isEditing ? (
                                                                                        <div className="flex gap-1 items-center">
                                                                                            <Input
                                                                                                type="date"
                                                                                                value={getDateRange(yearData)?.startDate || ''}
                                                                                                onChange={(e) => {
                                                                                                    handleFieldChange(
                                                                                                        [ctKey, item.term, yearValue.yearKey, yearDrKeys.dateRangeKey, yearDrKeys.startKey],
                                                                                                        e.target.value || null
                                                                                                    );
                                                                                                }}
                                                                                                className="h-6 text-xs w-24"
                                                                                                placeholder="Start"
                                                                                            />
                                                                                            <span className="text-xs">-</span>
                                                                                            <Input
                                                                                                type="date"
                                                                                                value={getDateRange(yearData)?.endDate || ''}
                                                                                                onChange={(e) => {
                                                                                                    handleFieldChange(
                                                                                                        [ctKey, item.term, yearValue.yearKey, yearDrKeys.dateRangeKey, yearDrKeys.endKey],
                                                                                                        e.target.value || null
                                                                                                    );
                                                                                                }}
                                                                                                className="h-6 text-xs w-24"
                                                                                                placeholder="End"
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="text-xs text-muted-foreground">{yearValue.dateRange || '-'}</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );})}
                                                                    </div>
                                                                );})}
                                                            </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Non-Energy Charges - Original format from smooth */}
                                        <div className="relative space-y-2">
                                            {reparsingSections['non_energy_charges'] && (
                                                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center z-20">
                                                    <div className="flex flex-col items-center gap-3 p-6 bg-background rounded-xl shadow-lg border">
                                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                        <p className="text-sm font-semibold">Reparsing Non-Energy Charges…</p>
                                                        <p className="text-xs text-muted-foreground">AI is extracting charges from the document</p>
                                                        <Button size="sm" variant="outline" className="mt-1" onClick={() => handleCancelSection('non_energy_charges')}>
                                                            <X className="h-3 w-3 mr-1" />Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between">
                                                <Label className="text-xs text-muted-foreground font-semibold">Non-Energy Charges</Label>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 px-2 text-xs text-purple-600 border-purple-200 hover:bg-purple-50 hover:text-purple-700"
                                                        disabled={!!reparsingSections['non_energy_charges']}
                                                        onClick={() => handleSectionReparse('non_energy_charges')}
                                                    >
                                                        {reparsingSections['non_energy_charges']
                                                            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                            : <RefreshCw className="h-3 w-3 mr-1" />
                                                        }
                                                        Reparse Non-Energy
                                                    </Button>
                                                    {isEditing ? (
                                                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                                                            onClick={() => setNewNECField({ label: '', value: '', unit: 'year' })}>
                                                            <Plus className="w-3 h-3 mr-1" />Add Field
                                                        </Button>
                                                    ) : (
                                                        <span className="text-[10px] text-muted-foreground/60 italic flex items-center gap-1">
                                                            <Plus className="w-2.5 h-2.5" />Edit to add fields
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {(() => {
                                                    const necKey = getNonEnergyChargesKey(activeRateData);
                                                    const charges = getNonEnergyCharges(activeRateData) || {};
                                                    // Render the 3 business-canonical fields FIRST and always — even when the
                                                    // offer document stated none of them — so a missing charge is visible and
                                                    // its configured default can be pulled in with the wand. Anything else the
                                                    // document carried is listed after, unchanged.
                                                    const canonicalKeys = CANONICAL_NEC_FIELDS.map(f => resolveNecFieldKey(charges, f.key));
                                                    // Extras only earn a row when they actually carry a value. Parsed documents
                                                    // routinely include placeholder keys set to null (market_fees, meter_agreement,
                                                    // …) for charges the retailer doesn't levy — rendering those puts empty boxes
                                                    // next to the 3 fields that matter, which is exactly what the canonical-field
                                                    // rework was meant to clean up.
                                                    const extraEntries = Object.entries(charges).filter(
                                                        ([k, v]) => !canonicalKeys.includes(k) && v !== null && v !== undefined
                                                    );

                                                    const renderRow = (
                                                        fieldKey: string,
                                                        label: string,
                                                        value: any,
                                                        canonical: CanonicalNecField | null,
                                                    ) => {
                                                        const hasValue = value !== null && value !== undefined;
                                                        const defaultValue = canonical ? resolveNecDefault(canonical) : null;
                                                        return (
                                                            <div key={fieldKey} className="flex items-center gap-1">
                                                                <Label className="text-xs flex-1 truncate" title={formatChargeLabel(label)}>
                                                                    {formatChargeLabel(label)}
                                                                </Label>
                                                                <Input
                                                                    type="number"
                                                                    value={value ?? ''}
                                                                    onChange={(e) => handleFieldChange(
                                                                        [activeRateSide, necKey, fieldKey],
                                                                        e.target.value ? parseFloat(e.target.value) : null
                                                                    )}
                                                                    disabled={!isEditing}
                                                                    className={`h-8 text-sm w-24 ${!hasValue ? 'bg-muted/30' : ''}`}
                                                                    step="0.01"
                                                                    placeholder={!hasValue ? '-' : ''}
                                                                />
                                                                {/* Rendered for all 3 canonical fields, disabled when no default applies.
                                                                    Hiding it instead made "this retailer has no default" (correct —
                                                                    e.g. Shell) look identical to "retailer didn't resolve" (a bug),
                                                                    with no way to tell them apart from the screen. */}
                                                                {isEditing && canonical && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-6 w-6 p-0 shrink-0 disabled:opacity-30"
                                                                        disabled={defaultValue === null}
                                                                        title={
                                                                            defaultValue !== null
                                                                                ? (canonical === 'meterAgreement'
                                                                                    ? `Apply standard Meter Agreement ($${defaultValue})`
                                                                                    : `Apply ${currentRetailerName || 'retailer'} default ($${defaultValue})`)
                                                                                : (currentRetailerName
                                                                                    ? `No default configured for ${currentRetailerName} — leave blank unless the offer states a value`
                                                                                    : 'Retailer not resolved for this offer — assign a retailer to enable defaults')
                                                                        }
                                                                        onClick={() => handleApplyNecDefault(canonical)}
                                                                    >
                                                                        <Wand2 className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                                {isEditing && !canonical && (
                                                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                                        onClick={() => handleDeleteNECField(fieldKey)}>
                                                                        <X className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <>
                                                            {CANONICAL_NEC_FIELDS.map((f, i) =>
                                                                renderRow(canonicalKeys[i], f.label, (charges as any)[canonicalKeys[i]] ?? null, f.key)
                                                            )}
                                                            {extraEntries.map(([charge, value]) => renderRow(charge, charge, value, null))}
                                                        </>
                                                    );
                                                })()}
                                                {isEditing && newNECField !== null && (
                                                    <div className="col-span-2 flex items-center gap-2 pt-1 border-t">
                                                        <Input
                                                            type="text"
                                                            value={newNECField.label}
                                                            onChange={(e) => setNewNECField({ ...newNECField, label: e.target.value })}
                                                            className="h-8 text-xs flex-1"
                                                            placeholder="Field name (e.g. Market Fees)"
                                                            autoFocus
                                                        />
                                                        <Input
                                                            type="number"
                                                            value={newNECField.value}
                                                            onChange={(e) => setNewNECField({ ...newNECField, value: e.target.value })}
                                                            className="h-8 text-sm w-20"
                                                            placeholder="0.00"
                                                            step="0.01"
                                                        />
                                                        <select
                                                            value={newNECField.unit}
                                                            onChange={(e) => setNewNECField({ ...newNECField, unit: e.target.value as 'year' | 'month' | 'day' })}
                                                            className="h-8 text-xs border border-input rounded-md px-1 bg-background"
                                                        >
                                                            <option value="year">$/yr</option>
                                                            <option value="month">$/mo</option>
                                                            <option value="day">$/day</option>
                                                        </select>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => {
                                                                if (!newNECField.label.trim()) return;
                                                                const multiplier = newNECField.unit === 'month' ? 12 : newNECField.unit === 'day' ? 365 : 1;
                                                                const annualValue = (parseFloat(newNECField.value) || 0) * multiplier;
                                                                handleAddNECField(newNECField.label.trim(), annualValue);
                                                            }}>
                                                            <CheckCircle className="w-3 h-3 text-green-500" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => setNewNECField(null)}>
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Environmental Charges - from active rate side (smooth or stepped) */}
                                        {/*
  ⚠️ RENDERED EVEN WHEN THE OFFER HAS NO ENVIRONMENTAL CHARGES.

  This section used to be gated on getEnvironmentalCharges(activeRateData), so an offer that
  arrived without any showed nothing at all — including the "Add New Environmental Charge"
  button, the one control that could have created the first one. A reviewer with a retailer's
  certificate table in front of them had no way to enter it.

  The list below already reads `|| {}`, so it draws nothing when there is nothing; only the
  gate had to go. Reported against a SmartestEnergy offer whose parse carried no environmental
  data at all.
*/}
{(
                                            <div className="relative space-y-4">
                                                {reparsingSections['environmental_charges'] && (
                                                    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center z-20">
                                                        <div className="flex flex-col items-center gap-3 p-6 bg-background rounded-xl shadow-lg border">
                                                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                            <p className="text-sm font-semibold">Reparsing Environmental Charges…</p>
                                                            <p className="text-xs text-muted-foreground">AI is extracting certificate data from the document</p>
                                                            <Button size="sm" variant="outline" className="mt-1" onClick={() => handleCancelSection('environmental_charges')}>
                                                                <X className="h-3 w-3 mr-1" />Cancel
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <Label className="text-sm text-muted-foreground font-semibold">Environmental Charges</Label>
                                                    <div className="flex items-center gap-1">
                                                        {isEditing && (
                                                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                                                onClick={() => setNewEnvScheme('')}>
                                                                <Plus className="w-3 h-3 mr-1" />Add New Environmental Charge
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 px-2 text-xs text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                                                            disabled={!!reparsingSections['environmental_charges']}
                                                            onClick={() => handleSectionReparse('environmental_charges')}
                                                        >
                                                            {reparsingSections['environmental_charges']
                                                                ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                : <RefreshCw className="h-3 w-3 mr-1" />
                                                            }
                                                            Reparse Env. Charges
                                                        </Button>
                                                    </div>
                                                </div>
                                                {isEditing && newEnvScheme !== null && (
                                                    <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/10">
                                                        <Input
                                                            type="text"
                                                            list="env-scheme-suggestions"
                                                            value={newEnvScheme}
                                                            onChange={(e) => setNewEnvScheme(e.target.value)}
                                                            className="h-8 text-xs flex-1"
                                                            placeholder="Scheme name (e.g. VEET, EEIS, or any custom name)"
                                                            autoFocus
                                                        />
                                                        <datalist id="env-scheme-suggestions">
                                                            {ENV_SCHEMES.map(s => <option key={s.key} value={s.displayKey} />)}
                                                        </datalist>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => handleAddEnvScheme(newEnvScheme)}>
                                                            <CheckCircle className="w-3 h-3 text-green-500" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => setNewEnvScheme(null)}>
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                )}
                                                {Object.keys(getEnvironmentalCharges(activeRateData) || {}).length === 0 && (
    <div className="text-xs text-muted-foreground border border-dashed rounded-lg px-3 py-4 text-center">
        No environmental charges on this offer.{' '}
        {isEditing
            ? 'Use "Add New Environmental Charge" above, or Reparse Env. Charges to read them from the document again.'
            : 'Click Edit to add one.'}
    </div>
)}
{Object.entries(getEnvironmentalCharges(activeRateData) || {}).map(([chargeType, yearData]: [string, any]) => {
                                                    const yearsWithData = getYearsWithData(yearData);
                                                    if (yearsWithData.length === 0 && !isEditing) return null;
                                                    const envKey = getEnvironmentalChargesKey(activeRateData);

                                                    return (
                                                        <div key={chargeType} className="space-y-3 p-3 bg-muted/20 rounded-lg border">
                                                            <div className="flex items-center justify-between">
                                                                <Label className="text-sm font-semibold">{resolveEnvSchemeLabel(chargeType)}</Label>
                                                                {isEditing && (
                                                                    <div className="flex items-center gap-1">
                                                                        {/* Fills the LOWEST free slot, so a parse that began at Year 2 gets a
                                                                            Year 1 box to type the missing first period into. */}
                                                                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs shrink-0"
                                                                            title={`Add a Rate Year to ${resolveEnvSchemeLabel(chargeType)} — fills the lowest missing year`}
                                                                            onClick={() => handleAddEnvYear(chargeType)}>
                                                                            <Plus className="w-3 h-3 mr-1" />Add Year
                                                                        </Button>
                                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                                            title={`Delete ${resolveEnvSchemeLabel(chargeType)}`}
                                                                            onClick={() => handleDeleteEnvScheme(chargeType)}>
                                                                            <X className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                                {getAllYearKeys(yearData).map((year) => {
                                                                    const data = yearData[year];
                                                                    if (!data) return null;
                                                                    const envFieldKeys = getEnvFieldKeys(data);
                                                                    return (
                                                                        <div key={year} className="space-y-2 p-3 bg-background rounded border">
                                                                            <div className="flex items-center justify-between gap-2">
                                                                                {isEditing ? (
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <Label className="text-sm font-semibold text-primary shrink-0">Year</Label>
                                                                                        <select
                                                                                            className="h-7 rounded border bg-background px-1 text-sm"
                                                                                            title="Move this year's rate, power % and certificate price to a different Year"
                                                                                            value={parseInt(year.slice(5), 10) || 1}
                                                                                            onChange={(e) => handleChangeEnvYearNumber(chargeType, year, parseInt(e.target.value, 10))}
                                                                                        >
                                                                                            {envYearOptions(yearData, year).map((n) => (
                                                                                                <option key={n} value={n}>{n}</option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                ) : (
                                                                                    <Label className="text-sm font-semibold text-primary">{formatYearLabel(year)}</Label>
                                                                                )}
                                                                                {isEditing && (
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        className="h-6 w-6 p-0 shrink-0"
                                                                                        title={`Delete ${formatYearLabel(year)} from ${resolveEnvSchemeLabel(chargeType)}`}
                                                                                        onClick={() => handleDeleteEnvYear(chargeType, year)}
                                                                                    >
                                                                                        <X className="w-3 h-3 text-destructive" />
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                            <div className="space-y-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label className="text-xs text-muted-foreground w-20">Rate/kWh:</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        value={data[envFieldKeys.rateKey] ?? ''}
                                                                                        onChange={(e) => handleFieldChange(
                                                                                            [activeRateSide, envKey, chargeType, year, envFieldKeys.rateKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        )}
                                                                                        disabled={!isEditing}
                                                                                        className="h-9 text-sm flex-1"
                                                                                        step={ENV_RATE_STEP}
                                                                                        placeholder="0.000000000"
                                                                                    />
                                                                                    {isEditing && (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="h-9 w-9 p-0 shrink-0"
                                                                                            title="Apply formula: (Cert Price × Power%) / 1000"
                                                                                            onClick={() => handleApplyFormula(activeRateSide, chargeType, year)}
                                                                                        >
                                                                                            <Wand2 className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label className="text-xs text-muted-foreground w-20">Power %:</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        value={data[envFieldKeys.percentKey] ?? ''}
                                                                                        onChange={(e) => handleFieldChange(
                                                                                            [activeRateSide, envKey, chargeType, year, envFieldKeys.percentKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        )}
                                                                                        disabled={!isEditing}
                                                                                        className="h-9 text-sm flex-1"
                                                                                        step={POWER_PCT_STEP}
                                                                                        placeholder="0.00"
                                                                                    />
                                                                                    {isEditing && (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="h-9 w-9 p-0 shrink-0"
                                                                                            title="Apply sys_config default Power% for this scheme/year"
                                                                                            onClick={() => handleApplyPowerDefault(activeRateSide, chargeType, year)}
                                                                                        >
                                                                                            <Wand2 className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label className="text-xs text-muted-foreground w-20">Cert Price:</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        value={data[envFieldKeys.certKey] ?? ''}
                                                                                        onChange={(e) => handleFieldChange(
                                                                                            [activeRateSide, envKey, chargeType, year, envFieldKeys.certKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        )}
                                                                                        disabled={!isEditing}
                                                                                        className="h-9 text-sm flex-1"
                                                                                        step={CERT_PRICE_STEP}
                                                                                        placeholder="0.00"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Render only smooth offer data fields (LEGACY FORMAT without contractTerms) */}
                                {!isNewContractTermsFormat(editedData) && editedData.smooth && (
                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-sm flex items-center gap-2">
                                            <div className="h-1 w-1 rounded-full bg-primary" />
                                            Smooth Rates
                                            {editedData.smooth.peakRates && (
                                                <Badge variant="outline" className="ml-2 text-xs">
                                                    {getYearsWithData(editedData.smooth.peakRates).length} year(s) of data
                                                </Badge>
                                            )}
                                        </h3>

                                        {/* Peak Rates */}
                                        {editedData.smooth.peakRates && (
                                            <div className="space-y-2">
                                                <Label className="text-xs text-muted-foreground">Peak Rates (c/kWh)</Label>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                                                    {getAllYearKeys(editedData.smooth.peakRates).map((year) => {
                                                        const rate = editedData.smooth.peakRates[year];
                                                        const hasValue = rate !== null && rate !== undefined;
                                                        return (
                                                            <div key={year}>
                                                                <Label className="text-xs">{formatYearLabel(year)}</Label>
                                                                <Input
                                                                    type="number"
                                                                    value={rate ?? ''}
                                                                    onChange={(e) => handleFieldChange(
                                                                        ['smooth', 'peakRates', year],
                                                                        e.target.value ? parseFloat(e.target.value) : null
                                                                    )}
                                                                    disabled={!isEditing}
                                                                    className={`h-8 text-sm ${!hasValue ? 'bg-muted/30 text-muted-foreground' : ''}`}
                                                                    step={ENERGY_RATE_STEP}
                                                                    placeholder={!hasValue ? '-' : ''}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Shoulder Rates (falls back to peak if not specified) */}
                                        {(editedData.smooth.shoulderRates || editedData.smooth.peakRates) && (
                                            <div className="space-y-2">
                                                <Label className="text-xs text-muted-foreground">
                                                    Shoulder Rates (c/kWh)
                                                    {!editedData.smooth.shoulderRates && (
                                                        <span className="ml-1 text-amber-600">(using peak as fallback)</span>
                                                    )}
                                                </Label>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                                                    {getAllYearKeys(editedData.smooth.shoulderRates || editedData.smooth.peakRates).map((year) => {
                                                        const hasShoulderRates = !!editedData.smooth.shoulderRates;
                                                        const shoulderValue = editedData.smooth.shoulderRates?.[year];
                                                        const peakValue = editedData.smooth.peakRates?.[year];
                                                        const displayValue = shoulderValue ?? peakValue ?? '';
                                                        const hasValue = displayValue !== null && displayValue !== undefined && displayValue !== '';
                                                        
                                                        return (
                                                            <div key={year}>
                                                                <Label className="text-xs">{formatYearLabel(year)}</Label>
                                                                <Input
                                                                    type="number"
                                                                    value={displayValue}
                                                                    onChange={(e) => handleFieldChange(
                                                                        ['smooth', 'shoulderRates', year],
                                                                        e.target.value ? parseFloat(e.target.value) : null
                                                                    )}
                                                                    disabled={!isEditing}
                                                                    className={`h-8 text-sm ${!hasShoulderRates ? 'bg-amber-50 border-amber-200' : ''} ${!hasValue ? 'bg-muted/30' : ''}`}
                                                                    step={ENERGY_RATE_STEP}
                                                                    placeholder={!hasValue ? '-' : ''}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Offpeak Rates */}
                                        {editedData.smooth.offpeakRates && (
                                            <div className="space-y-2">
                                                <Label className="text-xs text-muted-foreground">Offpeak Rates (c/kWh)</Label>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                                                    {getAllYearKeys(editedData.smooth.offpeakRates).map((year) => {
                                                        const rate = editedData.smooth.offpeakRates[year];
                                                        const hasValue = rate !== null && rate !== undefined;
                                                        return (
                                                            <div key={year}>
                                                                <Label className="text-xs">{formatYearLabel(year)}</Label>
                                                                <Input
                                                                    type="number"
                                                                    value={rate ?? ''}
                                                                    onChange={(e) => handleFieldChange(
                                                                        ['smooth', 'offpeakRates', year],
                                                                        e.target.value ? parseFloat(e.target.value) : null
                                                                    )}
                                                                    disabled={!isEditing}
                                                                    className={`h-8 text-sm ${!hasValue ? 'bg-muted/30 text-muted-foreground' : ''}`}
                                                                    step={ENERGY_RATE_STEP}
                                                                    placeholder={!hasValue ? '-' : ''}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <Separator />

                                        {/* Non-Energy Charges */}
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-xs text-muted-foreground font-semibold">Non-Energy Charges</Label>
                                                {isEditing ? (
                                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                                                        onClick={() => setNewNECField({ label: '', value: '', unit: 'year' })}>
                                                        <Plus className="w-3 h-3 mr-1" />Add Field
                                                    </Button>
                                                ) : (
                                                    <span className="text-[10px] text-muted-foreground/60 italic flex items-center gap-1">
                                                        <Plus className="w-2.5 h-2.5" />Edit to add fields
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {(() => {
                                                    const necKey = getNonEnergyChargesKey(activeRateData);
                                                    const charges = getNonEnergyCharges(activeRateData) || {};
                                                    // Render the 3 business-canonical fields FIRST and always — even when the
                                                    // offer document stated none of them — so a missing charge is visible and
                                                    // its configured default can be pulled in with the wand. Anything else the
                                                    // document carried is listed after, unchanged.
                                                    const canonicalKeys = CANONICAL_NEC_FIELDS.map(f => resolveNecFieldKey(charges, f.key));
                                                    // Extras only earn a row when they actually carry a value. Parsed documents
                                                    // routinely include placeholder keys set to null (market_fees, meter_agreement,
                                                    // …) for charges the retailer doesn't levy — rendering those puts empty boxes
                                                    // next to the 3 fields that matter, which is exactly what the canonical-field
                                                    // rework was meant to clean up.
                                                    const extraEntries = Object.entries(charges).filter(
                                                        ([k, v]) => !canonicalKeys.includes(k) && v !== null && v !== undefined
                                                    );

                                                    const renderRow = (
                                                        fieldKey: string,
                                                        label: string,
                                                        value: any,
                                                        canonical: CanonicalNecField | null,
                                                    ) => {
                                                        const hasValue = value !== null && value !== undefined;
                                                        const defaultValue = canonical ? resolveNecDefault(canonical) : null;
                                                        return (
                                                            <div key={fieldKey} className="flex items-center gap-1">
                                                                <Label className="text-xs flex-1 truncate" title={formatChargeLabel(label)}>
                                                                    {formatChargeLabel(label)}
                                                                </Label>
                                                                <Input
                                                                    type="number"
                                                                    value={value ?? ''}
                                                                    onChange={(e) => handleFieldChange(
                                                                        [activeRateSide, necKey, fieldKey],
                                                                        e.target.value ? parseFloat(e.target.value) : null
                                                                    )}
                                                                    disabled={!isEditing}
                                                                    className={`h-8 text-sm w-24 ${!hasValue ? 'bg-muted/30' : ''}`}
                                                                    step="0.01"
                                                                    placeholder={!hasValue ? '-' : ''}
                                                                />
                                                                {/* Rendered for all 3 canonical fields, disabled when no default applies.
                                                                    Hiding it instead made "this retailer has no default" (correct —
                                                                    e.g. Shell) look identical to "retailer didn't resolve" (a bug),
                                                                    with no way to tell them apart from the screen. */}
                                                                {isEditing && canonical && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-6 w-6 p-0 shrink-0 disabled:opacity-30"
                                                                        disabled={defaultValue === null}
                                                                        title={
                                                                            defaultValue !== null
                                                                                ? (canonical === 'meterAgreement'
                                                                                    ? `Apply standard Meter Agreement ($${defaultValue})`
                                                                                    : `Apply ${currentRetailerName || 'retailer'} default ($${defaultValue})`)
                                                                                : (currentRetailerName
                                                                                    ? `No default configured for ${currentRetailerName} — leave blank unless the offer states a value`
                                                                                    : 'Retailer not resolved for this offer — assign a retailer to enable defaults')
                                                                        }
                                                                        onClick={() => handleApplyNecDefault(canonical)}
                                                                    >
                                                                        <Wand2 className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                                {isEditing && !canonical && (
                                                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                                        onClick={() => handleDeleteNECField(fieldKey)}>
                                                                        <X className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <>
                                                            {CANONICAL_NEC_FIELDS.map((f, i) =>
                                                                renderRow(canonicalKeys[i], f.label, (charges as any)[canonicalKeys[i]] ?? null, f.key)
                                                            )}
                                                            {extraEntries.map(([charge, value]) => renderRow(charge, charge, value, null))}
                                                        </>
                                                    );
                                                })()}
                                                {isEditing && newNECField !== null && (
                                                    <div className="col-span-2 flex items-center gap-2 pt-1 border-t">
                                                        <Input
                                                            type="text"
                                                            value={newNECField.label}
                                                            onChange={(e) => setNewNECField({ ...newNECField, label: e.target.value })}
                                                            className="h-8 text-xs flex-1"
                                                            placeholder="Field name (e.g. Market Fees)"
                                                            autoFocus
                                                        />
                                                        <Input
                                                            type="number"
                                                            value={newNECField.value}
                                                            onChange={(e) => setNewNECField({ ...newNECField, value: e.target.value })}
                                                            className="h-8 text-sm w-20"
                                                            placeholder="0.00"
                                                            step="0.01"
                                                        />
                                                        <select
                                                            value={newNECField.unit}
                                                            onChange={(e) => setNewNECField({ ...newNECField, unit: e.target.value as 'year' | 'month' | 'day' })}
                                                            className="h-8 text-xs border border-input rounded-md px-1 bg-background"
                                                        >
                                                            <option value="year">$/yr</option>
                                                            <option value="month">$/mo</option>
                                                            <option value="day">$/day</option>
                                                        </select>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => {
                                                                if (!newNECField.label.trim()) return;
                                                                const multiplier = newNECField.unit === 'month' ? 12 : newNECField.unit === 'day' ? 365 : 1;
                                                                const annualValue = (parseFloat(newNECField.value) || 0) * multiplier;
                                                                handleAddNECField(newNECField.label.trim(), annualValue);
                                                            }}>
                                                            <CheckCircle className="w-3 h-3 text-green-500" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => setNewNECField(null)}>
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Environmental Charges */}
                                        {/*
  ⚠️ RENDERED EVEN WHEN THE OFFER HAS NO ENVIRONMENTAL CHARGES.

  This section used to be gated on getEnvironmentalCharges(activeRateData), so an offer that
  arrived without any showed nothing at all — including the "Add New Environmental Charge"
  button, the one control that could have created the first one. A reviewer with a retailer's
  certificate table in front of them had no way to enter it.

  The list below already reads `|| {}`, so it draws nothing when there is nothing; only the
  gate had to go. Reported against a SmartestEnergy offer whose parse carried no environmental
  data at all.
*/}
{(
                                            <div className="relative space-y-4">
                                                {reparsingSections['environmental_charges'] && (
                                                    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center z-20">
                                                        <div className="flex flex-col items-center gap-3 p-6 bg-background rounded-xl shadow-lg border">
                                                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                            <p className="text-sm font-semibold">Reparsing Environmental Charges…</p>
                                                            <p className="text-xs text-muted-foreground">AI is extracting certificate data from the document</p>
                                                            <Button size="sm" variant="outline" className="mt-1" onClick={() => handleCancelSection('environmental_charges')}>
                                                                <X className="h-3 w-3 mr-1" />Cancel
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <Label className="text-sm text-muted-foreground font-semibold">Environmental Charges</Label>
                                                    <div className="flex items-center gap-1">
                                                        {isEditing && (
                                                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                                                onClick={() => setNewEnvScheme('')}>
                                                                <Plus className="w-3 h-3 mr-1" />Add New Environmental Charge
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 px-2 text-xs text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                                                            disabled={!!reparsingSections['environmental_charges']}
                                                            onClick={() => handleSectionReparse('environmental_charges')}
                                                        >
                                                            {reparsingSections['environmental_charges']
                                                                ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                : <RefreshCw className="h-3 w-3 mr-1" />
                                                            }
                                                            Reparse Env. Charges
                                                        </Button>
                                                    </div>
                                                </div>
                                                {isEditing && newEnvScheme !== null && (
                                                    <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/10">
                                                        <Input
                                                            type="text"
                                                            list="env-scheme-suggestions"
                                                            value={newEnvScheme}
                                                            onChange={(e) => setNewEnvScheme(e.target.value)}
                                                            className="h-8 text-xs flex-1"
                                                            placeholder="Scheme name (e.g. VEET, EEIS, or any custom name)"
                                                            autoFocus
                                                        />
                                                        <datalist id="env-scheme-suggestions">
                                                            {ENV_SCHEMES.map(s => <option key={s.key} value={s.displayKey} />)}
                                                        </datalist>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => handleAddEnvScheme(newEnvScheme)}>
                                                            <CheckCircle className="w-3 h-3 text-green-500" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                            onClick={() => setNewEnvScheme(null)}>
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                )}
                                                {Object.keys(getEnvironmentalCharges(activeRateData) || {}).length === 0 && (
    <div className="text-xs text-muted-foreground border border-dashed rounded-lg px-3 py-4 text-center">
        No environmental charges on this offer.{' '}
        {isEditing
            ? 'Use "Add New Environmental Charge" above, or Reparse Env. Charges to read them from the document again.'
            : 'Click Edit to add one.'}
    </div>
)}
{Object.entries(getEnvironmentalCharges(activeRateData) || {}).map(([chargeType, yearData]: [string, any]) => {
                                                    const yearsWithData = getYearsWithData(yearData);
                                                    if (yearsWithData.length === 0 && !isEditing) return null;
                                                    const envKey = getEnvironmentalChargesKey(activeRateData);

                                                    return (
                                                        <div key={chargeType} className="space-y-3 p-3 bg-muted/20 rounded-lg border">
                                                            <div className="flex items-center justify-between">
                                                                <Label className="text-sm font-semibold">{resolveEnvSchemeLabel(chargeType)}</Label>
                                                                {isEditing && (
                                                                    <div className="flex items-center gap-1">
                                                                        {/* Fills the LOWEST free slot, so a parse that began at Year 2 gets a
                                                                            Year 1 box to type the missing first period into. */}
                                                                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs shrink-0"
                                                                            title={`Add a Rate Year to ${resolveEnvSchemeLabel(chargeType)} — fills the lowest missing year`}
                                                                            onClick={() => handleAddEnvYear(chargeType)}>
                                                                            <Plus className="w-3 h-3 mr-1" />Add Year
                                                                        </Button>
                                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                                                            title={`Delete ${resolveEnvSchemeLabel(chargeType)}`}
                                                                            onClick={() => handleDeleteEnvScheme(chargeType)}>
                                                                            <X className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                                {getAllYearKeys(yearData).map((year) => {
                                                                    const data = yearData[year];
                                                                    if (!data) return null;
                                                                    const envFieldKeys = getEnvFieldKeys(data);
                                                                    return (
                                                                        <div key={year} className="space-y-2 p-3 bg-background rounded border">
                                                                            <div className="flex items-center justify-between gap-2">
                                                                                {isEditing ? (
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <Label className="text-sm font-semibold text-primary shrink-0">Year</Label>
                                                                                        <select
                                                                                            className="h-7 rounded border bg-background px-1 text-sm"
                                                                                            title="Move this year's rate, power % and certificate price to a different Year"
                                                                                            value={parseInt(year.slice(5), 10) || 1}
                                                                                            onChange={(e) => handleChangeEnvYearNumber(chargeType, year, parseInt(e.target.value, 10))}
                                                                                        >
                                                                                            {envYearOptions(yearData, year).map((n) => (
                                                                                                <option key={n} value={n}>{n}</option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                ) : (
                                                                                    <Label className="text-sm font-semibold text-primary">{formatYearLabel(year)}</Label>
                                                                                )}
                                                                                {isEditing && (
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        className="h-6 w-6 p-0 shrink-0"
                                                                                        title={`Delete ${formatYearLabel(year)} from ${resolveEnvSchemeLabel(chargeType)}`}
                                                                                        onClick={() => handleDeleteEnvYear(chargeType, year)}
                                                                                    >
                                                                                        <X className="w-3 h-3 text-destructive" />
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                            <div className="space-y-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label className="text-xs text-muted-foreground w-20">Rate/kWh:</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        value={data[envFieldKeys.rateKey] ?? ''}
                                                                                        onChange={(e) => handleFieldChange(
                                                                                            [activeRateSide, envKey, chargeType, year, envFieldKeys.rateKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        )}
                                                                                        disabled={!isEditing}
                                                                                        className="h-9 text-sm flex-1"
                                                                                        step={ENV_RATE_STEP}
                                                                                        placeholder="0.000000000"
                                                                                    />
                                                                                    {isEditing && (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="h-9 w-9 p-0 shrink-0"
                                                                                            title="Apply formula: (Cert Price × Power%) / 1000"
                                                                                            onClick={() => handleApplyFormula(activeRateSide, chargeType, year)}
                                                                                        >
                                                                                            <Wand2 className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label className="text-xs text-muted-foreground w-20">Power %:</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        value={data[envFieldKeys.percentKey] ?? ''}
                                                                                        onChange={(e) => handleFieldChange(
                                                                                            [activeRateSide, envKey, chargeType, year, envFieldKeys.percentKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        )}
                                                                                        disabled={!isEditing}
                                                                                        className="h-9 text-sm flex-1"
                                                                                        step={POWER_PCT_STEP}
                                                                                        placeholder="0.00"
                                                                                    />
                                                                                    {isEditing && (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="h-9 w-9 p-0 shrink-0"
                                                                                            title="Apply sys_config default Power% for this scheme/year"
                                                                                            onClick={() => handleApplyPowerDefault(activeRateSide, chargeType, year)}
                                                                                        >
                                                                                            <Wand2 className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label className="text-xs text-muted-foreground w-20">Cert Price:</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        value={data[envFieldKeys.certKey] ?? ''}
                                                                                        onChange={(e) => handleFieldChange(
                                                                                            [activeRateSide, envKey, chargeType, year, envFieldKeys.certKey],
                                                                                            e.target.value ? parseFloat(e.target.value) : null
                                                                                        )}
                                                                                        disabled={!isEditing}
                                                                                        className="h-9 text-sm flex-1"
                                                                                        step={CERT_PRICE_STEP}
                                                                                        placeholder="0.00"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!isNewContractTermsFormat(editedData) && !editedData.smooth && (
                                    <div className="text-center text-muted-foreground py-8">
                                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
                                        <p className="text-sm">No smooth rate data available</p>
                                        <p className="text-xs mt-1">Only smooth rates are displayed for this review</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Footer: Action Buttons */}
            <div className="sticky bottom-0 bg-white flex items-center justify-between pt-4 pb-4 border-t z-10">
                <div className="flex items-center gap-2">
                    {hasChanges && (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                            <Edit3 className="h-3 w-3 mr-1" />
                            Unsaved Changes
                        </Badge>
                    )}
                </div>

                <div className="flex gap-2 items-center">
                    {onReparse && (
                        <Button variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700" onClick={() => onReparse(currentOffer.id)}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Reparse All
                        </Button>
                    )}
                    {isEditing && hasChanges && (
                        <Button
                            variant="outline"
                            onClick={() => setEditedData(currentOffer.parsedData || {})}
                        >
                            <Save className="h-4 w-4 mr-2" />
                            Reset Changes
                        </Button>
                    )}
                    {/* Two terms cannot both be the same contract year: the review screen would
                        show two identically named tabs and the generated document two sections
                        under one heading. The picker still OFFERS every year — hiding the one
                        someone was reaching for is worse — so the clash is caught here instead.
                        Only enforced once the reviewer has assigned at least one term, so the
                        existing unconventional offers (where a sub-year stub deliberately shares
                        Year 1) stay approvable. See duplicateTermYears. */}
                    {termYearConflicts.length > 0 && (
                        <span className="text-xs text-destructive font-medium self-center max-w-[280px]">
                            {termYearConflicts.length === 1
                                ? `Two terms are both set to Year ${termYearConflicts[0]}.`
                                : `Terms clash on Year ${termYearConflicts.join(', Year ')}.`}
                            {' '}Give each term its own year to continue.
                        </span>
                    )}
                    <Button
                        onClick={handleSaveAndApprove}
                        disabled={(isReviewed && !hasChanges) || termYearConflicts.length > 0}
                        title={termYearConflicts.length > 0
                            ? 'Two terms are assigned the same contract year'
                            : undefined}
                        className="min-w-[140px]"
                    >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {isReviewed ? "Update & Continue" : "Approve & Continue"}
                    </Button>
                </div>
            </div>
        </div>
    );
});
